// src/pages/AlignersPage.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import Layout from '../components/Layout';
import { useToast } from '../contexts/ToastContext';

import {
  Activity,
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Filter,
  MessageCircle,
  RefreshCw,
  Search,
  Settings2,
  ShieldAlert,
  UserRound,
  X,
} from 'lucide-react';

const API_URL =
  (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TREATMENT_STATUS = {
  planned: 'Planejado',
  in_progress: 'Em andamento',
  paused: 'Pausado',
  refinement: 'Em refinamento',
  completed: 'Concluído',
  cancelled: 'Cancelado',
};

const CURRENT_SITUATIONS = {
  awaiting_documents: 'Aguardando documentação/exames',
  awaiting_aligners: 'Aguardando alinhadores',
  active_delivery: 'Em tratamento e recebendo alinhadores',
  follow_up_only: 'Em acompanhamento',
  awaiting_retention: 'Aguardando contenção',
  final_follow_up: 'Todos os alinhadores já foram entregues',
  awaiting_refinement: 'Aguardando refinamento / novos alinhadores',
  refinement_active: 'Em refinamento e recebendo alinhadores',
  completed: 'Tratamento concluído',
  paused: 'Tratamento pausado',
  cancelled: 'Tratamento cancelado',
};

const SIMPLE_SITUATIONS = new Set([
  'awaiting_documents',
  'awaiting_aligners',
  'follow_up_only',
  'awaiting_refinement',
  'refinement_active',
  'awaiting_retention',
  'completed',
]);

const getSimpleSituation = (item) => {
  if (item?.status === 'completed') return 'completed';
  if (item?.phase_status === 'final_follow_up') return 'follow_up_only';
  return SIMPLE_SITUATIONS.has(item?.phase_status)
    ? item.phase_status
    : 'follow_up_only';
};

const hasAllAlignersDelivered = (item) =>
  item?.all_aligners_delivered === true ||
  item?.phase_status === 'final_follow_up';

const MONITORING_CONFIG = {
  on_time: {
    label: 'Em dia',
    description: 'Sem pendência de acompanhamento',
    cardClass: 'border-green-200 bg-green-50',
    badgeClass: 'bg-green-100 text-green-800',
    icon: CheckCircle2,
  },

  attention: {
    label: 'Atenção',
    description: 'Prazo próximo e sem retorno futuro',
    cardClass: 'border-yellow-200 bg-yellow-50',
    badgeClass: 'bg-yellow-100 text-yellow-800',
    icon: Clock3,
  },

  overdue: {
    label: 'Vencido',
    description: 'Prazo de acompanhamento já passou',
    cardClass: 'border-orange-200 bg-orange-50',
    badgeClass: 'bg-orange-100 text-orange-800',
    icon: AlertCircle,
  },

  urgent: {
    label: 'Urgente',
    description: 'Acompanhamento bastante atrasado',
    cardClass: 'border-red-200 bg-red-50',
    badgeClass: 'bg-red-100 text-red-800',
    icon: ShieldAlert,
  },

  needs_review: {
    label: 'Revisar acompanhamento',
    description: 'Falta uma data clínica confiável',
    cardClass: 'border-purple-200 bg-purple-50',
    badgeClass: 'bg-purple-100 text-purple-800',
    icon: Settings2,
  },

  no_reference: {
    label: 'Sem referência',
    cardClass: 'border-gray-200 bg-gray-50',
    badgeClass: 'bg-gray-100 text-gray-700',
    icon: AlertCircle,
  },

  paused: {
    label: 'Pausado',
    cardClass: 'border-gray-200 bg-gray-50',
    badgeClass: 'bg-gray-100 text-gray-700',
    icon: Clock3,
  },

  closed: {
    label: 'Encerrado',
    cardClass: 'border-gray-200 bg-gray-50',
    badgeClass: 'bg-gray-100 text-gray-700',
    icon: CheckCircle2,
  },
};

const RECOMMENDED_ACTION_TONES = {
  blue: {
    container:
      'border-blue-200 bg-blue-50',
    icon:
      'bg-blue-100 text-blue-700',
    title:
      'text-blue-900',
    description:
      'text-blue-700',
  },

  green: {
    container:
      'border-green-200 bg-green-50',
    icon:
      'bg-green-100 text-green-700',
    title:
      'text-green-900',
    description:
      'text-green-700',
  },

  amber: {
    container:
      'border-amber-200 bg-amber-50',
    icon:
      'bg-amber-100 text-amber-700',
    title:
      'text-amber-900',
    description:
      'text-amber-700',
  },

  orange: {
    container:
      'border-orange-200 bg-orange-50',
    icon:
      'bg-orange-100 text-orange-700',
    title:
      'text-orange-900',
    description:
      'text-orange-700',
  },

  red: {
    container:
      'border-red-200 bg-red-50',
    icon:
      'bg-red-100 text-red-700',
    title:
      'text-red-900',
    description:
      'text-red-700',
  },

  gray: {
    container:
      'border-gray-200 bg-gray-50',
    icon:
      'bg-gray-200 text-gray-600',
    title:
      'text-gray-900',
    description:
      'text-gray-600',
  },
};

const TREATMENT_HEALTH_CONFIG = {
  healthy: {
    label: 'Saudável',
    icon: CheckCircle2,
    containerClass:
      'border-green-200 bg-green-50',
    iconClass:
      'bg-green-100 text-green-700',
    titleClass:
      'text-green-900',
    textClass:
      'text-green-700',
    badgeClass:
      'bg-green-100 text-green-800',
  },

  attention: {
    label: 'Atenção',
    icon: Clock3,
    containerClass:
      'border-amber-200 bg-amber-50',
    iconClass:
      'bg-amber-100 text-amber-700',
    titleClass:
      'text-amber-900',
    textClass:
      'text-amber-700',
    badgeClass:
      'bg-amber-100 text-amber-800',
  },

  critical: {
    label: 'Crítico',
    icon: ShieldAlert,
    containerClass:
      'border-red-200 bg-red-50',
    iconClass:
      'bg-red-100 text-red-700',
    titleClass:
      'text-red-900',
    textClass:
      'text-red-700',
    badgeClass:
      'bg-red-100 text-red-800',
  },

  preparation: {
    label: 'Em preparação',
    icon: Settings2,
    containerClass:
      'border-blue-200 bg-blue-50',
    iconClass:
      'bg-blue-100 text-blue-700',
    titleClass:
      'text-blue-900',
    textClass:
      'text-blue-700',
    badgeClass:
      'bg-blue-100 text-blue-800',
  },

  paused: {
    label: 'Pausado',
    icon: Clock3,
    containerClass:
      'border-gray-200 bg-gray-50',
    iconClass:
      'bg-gray-200 text-gray-600',
    titleClass:
      'text-gray-900',
    textClass:
      'text-gray-600',
    badgeClass:
      'bg-gray-200 text-gray-700',
  },

  closed: {
    label: 'Encerrado',
    icon: CheckCircle2,
    containerClass:
      'border-gray-200 bg-gray-50',
    iconClass:
      'bg-gray-200 text-gray-600',
    titleClass:
      'text-gray-900',
    textClass:
      'text-gray-600',
    badgeClass:
      'bg-gray-200 text-gray-700',
  },
};

const REFERENCE_SOURCE = {
  aligner_event: 'Evento de alinhadores',
  completed_appointment: 'Agenda do OdontoAssist',
  manual: 'Data informada manualmente',
  imported: 'Importação SouSmile',
  treatment_start: 'Início do tratamento',
  none: 'Sem referência',
};

const OPERATIONAL_FILTERS = {
  all: 'Todas as situações',
  scheduled_return: 'Com retorno agendado',
  return_today: 'Retorno agendado para hoje',
  no_scheduled_return: 'Sem retorno agendado',
  next_set_received: 'Próximo conjunto recebido',
  next_set_pending: 'Próximo conjunto não recebido',
  initial_actions_pending: 'Ações iniciais pendentes',
  overdue_or_urgent: 'Vencidos ou urgentes',
  active_treatments: 'Tratamentos ativos',
};

const RECOMMENDED_ACTION_FILTERS = {
  all: 'Todas as ações recomendadas',
  any_pending: 'Qualquer ação recomendada pendente',
  schedule_return: 'Solicitar agendamento',
  initial_guidance: 'Enviar orientações iniciais',
  follow_up: 'Acompanhar adaptação',
  first_change: 'Acompanhar primeira troca',
  scheduled_return: 'Reforçar retorno agendado',
};

const RECOMMENDED_ACTION_CARDS = {
  schedule_return: {
    label: 'Solicitar agendamento',
    description: 'Pacientes sem retorno futuro',
    icon: CalendarDays,
    cardClass:
      'border-red-200 bg-red-50 text-red-900',
    iconClass:
      'bg-red-100 text-red-700',
    valueClass:
      'text-red-900',
  },

  initial_guidance: {
    label: 'Orientações iniciais',
    description: 'Primeira orientação pendente',
    icon: MessageCircle,
    cardClass:
      'border-amber-200 bg-amber-50 text-amber-900',
    iconClass:
      'bg-amber-100 text-amber-700',
    valueClass:
      'text-amber-900',
  },

  follow_up: {
    label: 'Acompanhar adaptação',
    description: 'Contato de adaptação pendente',
    icon: Activity,
    cardClass:
      'border-purple-200 bg-purple-50 text-purple-900',
    iconClass:
      'bg-purple-100 text-purple-700',
    valueClass:
      'text-purple-900',
  },

  first_change: {
    label: 'Primeira troca',
    description: 'Acompanhamento ainda pendente',
    icon: RefreshCw,
    cardClass:
      'border-cyan-200 bg-cyan-50 text-cyan-900',
    iconClass:
      'bg-cyan-100 text-cyan-700',
    valueClass:
      'text-cyan-900',
  },

  scheduled_return: {
    label: 'Retornos agendados',
    description: 'Lembrete de retorno disponível',
    icon: Clock3,
    cardClass:
      'border-green-200 bg-green-50 text-green-900',
    iconClass:
      'bg-green-100 text-green-700',
    valueClass:
      'text-green-900',
  },
};

const PRODUCTION_PANEL_CARDS = {
  waiting_delivery: {
    label: 'Conjuntos na clínica',
    description:
      'Controle interno dos lotes recebidos',
    icon: CheckCircle2,
    cardClass:
      'border-blue-200 bg-blue-50',
    iconClass:
      'bg-blue-100 text-blue-700',
    valueClass:
      'text-blue-900',
    operationalFilter: 'next_set_received',
  },

  returns_today: {
    label: 'Retornos hoje',
    description:
      'Consultas de alinhadores previstas para hoje',
    icon: CalendarDays,
    cardClass:
      'border-green-200 bg-green-50',
    iconClass:
      'bg-green-100 text-green-700',
    valueClass:
      'text-green-900',
    operationalFilter: 'return_today',
  },

  delayed: {
    label: 'Acompanhamentos atrasados',
    description:
      'Pacientes vencidos ou urgentes',
    icon: ShieldAlert,
    cardClass:
      'border-red-200 bg-red-50',
    iconClass:
      'bg-red-100 text-red-700',
    valueClass:
      'text-red-900',
    operationalFilter: 'overdue_or_urgent',
  },

  pending_messages: {
    label: 'Mensagens pendentes',
    description:
      'Intervenções recomendadas ainda disponíveis',
    icon: MessageCircle,
    cardClass:
      'border-amber-200 bg-amber-50',
    iconClass:
      'bg-amber-100 text-amber-700',
    valueClass:
      'text-amber-900',
    operationalFilter: null,
  },

  active_treatments: {
    label: 'Tratamentos ativos',
    description:
      'Planejados, em andamento, pausados ou em refinamento',
    icon: Activity,
    cardClass:
      'border-purple-200 bg-purple-50',
    iconClass:
      'bg-purple-100 text-purple-700',
    valueClass:
      'text-purple-900',
    operationalFilter: 'active_treatments',
  },
};

const HEALTH_PANEL_CARDS = {
  critical: {
    label: 'Críticos',
    description:
      'Tratamentos que exigem intervenção prioritária',
    icon: ShieldAlert,
    cardClass:
      'border-red-200 bg-red-50',
    iconClass:
      'bg-red-100 text-red-700',
    valueClass:
      'text-red-900',
  },

  attention: {
    label: 'Em atenção',
    description:
      'Tratamentos com pendências ou prazos próximos',
    icon: Clock3,
    cardClass:
      'border-amber-200 bg-amber-50',
    iconClass:
      'bg-amber-100 text-amber-700',
    valueClass:
      'text-amber-900',
  },

  healthy: {
    label: 'Saudáveis',
    description:
      'Acompanhamentos dentro das condições esperadas',
    icon: CheckCircle2,
    cardClass:
      'border-green-200 bg-green-50',
    iconClass:
      'bg-green-100 text-green-700',
    valueClass:
      'text-green-900',
  },

  not_scored: {
    label: 'Não avaliados',
    description:
      'Planejados, pausados ou tratamentos encerrados',
    icon: Settings2,
    cardClass:
      'border-gray-200 bg-gray-50',
    iconClass:
      'bg-gray-200 text-gray-600',
    valueClass:
      'text-gray-900',
  },
};

const HEALTH_FILTERS = {
  all: 'Todas as classificações',
  critical: 'Críticos',
  attention: 'Em atenção',
  healthy: 'Saudáveis',
  not_scored: 'Não avaliados',
};

const SORT_OPTIONS = {
  priority: 'Prioridade clínica',
  health_ascending: 'Pior saúde primeiro',
  health_descending: 'Melhor saúde primeiro',
  next_action: 'Data da próxima ação',
  patient: 'Nome do paciente',
};

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  },
});

const formatDateBR = (value) => {
  if (!value) {
    return 'Não informada';
  }

  const normalized = String(value).slice(0, 10);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return value;
  }

  const [year, month, day] = normalized.split('-');

  return `${day}/${month}/${year}`;
};

const parseLocalDate = (value) => {
  if (!value) {
    return null;
  }

  const normalized = String(value).slice(0, 10);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }

  const [year, month, day] = normalized
    .split('-')
    .map(Number);

  return new Date(year, month - 1, day);
};

const startOfToday = () => {
  const today = new Date();

  return new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
};

const isSameLocalDate = (
  firstValue,
  secondValue = startOfToday()
) => {
  const firstDate = parseLocalDate(firstValue);

  if (!firstDate || !secondValue) {
    return false;
  }

  return (
    firstDate.getFullYear() ===
      secondValue.getFullYear() &&
    firstDate.getMonth() ===
      secondValue.getMonth() &&
    firstDate.getDate() ===
      secondValue.getDate()
  );
};

const differenceInCalendarDays = (
  targetValue,
  referenceDate = startOfToday()
) => {
  const targetDate = parseLocalDate(targetValue);

  if (!targetDate) {
    return null;
  }

  const millisecondsPerDay = 24 * 60 * 60 * 1000;

  return Math.round(
    (targetDate.getTime() - referenceDate.getTime()) /
      millisecondsPerDay
  );
};

const getDeadlineText = (value) => {
  const days = differenceInCalendarDays(value);

  if (days === null) {
    return 'Prazo não calculado';
  }

  if (days === 0) {
    return 'Ação prevista para hoje';
  }

  if (days === 1) {
    return 'Ação prevista para amanhã';
  }

  if (days > 1) {
    return `Faltam ${days} dias`;
  }

  if (days === -1) {
    return 'Atrasado há 1 dia';
  }

  return `Atrasado há ${Math.abs(days)} dias`;
};

const getOperationalPriority = (item) => {
  const statusWeight = {
    urgent: 0,
    overdue: 1,
    attention: 2,
    needs_review: 3,
    on_time: 4,
    no_reference: 5,
  };

  return statusWeight[item?.monitoring_status] ?? 6;
};

const getTreatmentCardClass = (
  item,
  nextSetWaitingDelivery
) => {
  switch (item?.monitoring_status) {
    case 'urgent':
      return `
        border-red-300
        bg-red-50/50
        ring-1
        ring-red-100
      `;

    case 'overdue':
      return `
        border-orange-300
        bg-orange-50/40
      `;

    case 'attention':
      return `
        border-amber-300
        bg-amber-50/40
      `;

    case 'needs_review':
      return `
        border-purple-300
        bg-purple-50/40
      `;

    case 'on_time':
      return `
        border-green-200
        bg-green-50/30
      `;

    case 'no_reference':
      return `
        border-gray-300
        bg-gray-50
      `;

    default:
      return `
        border-gray-200
        bg-white
      `;
  }
};

const hasInitialActionsPending = (item) =>
  item?.phase_status === 'active_delivery' &&
  (
    item?.initial_guidance_completed !== true ||
    item?.adaptation_message_sent !== true ||
    item?.first_aligner_change_followed !== true
  );

const getTreatmentTimelineSteps = (item) => {
  const treatmentStatus =
    item?.status ||
    item?.treatment_status;

  const hasCurrentSet =
    item?.current_set_start_stage !== null &&
    item?.current_set_start_stage !== undefined;

  const nextSetReceived =
    item?.next_set_received === true &&
    item?.next_set_start_stage !== null &&
    item?.next_set_start_stage !== undefined;

  const nextSetConfigured =
    item?.next_set_start_stage !== null &&
    item?.next_set_start_stage !== undefined;

  const completed =
    treatmentStatus === 'completed';

  const allAlignersDelivered = hasAllAlignersDelivered(item);

  return [
    {
      id: 'initial_guidance',
      label: 'Orientações iniciais',
      completed:
        item?.initial_guidance_completed === true,
      active:
        item?.initial_guidance_completed !== true,
    },

    {
      id: 'adaptation',
      label: 'Adaptação',
      completed:
        item?.adaptation_message_sent === true,
      active:
        item?.initial_guidance_completed === true &&
        item?.adaptation_message_sent !== true,
    },

    {
      id: 'first_change',
      label: 'Primeira troca',
      completed:
        item?.first_aligner_change_followed === true,
      active:
        item?.adaptation_message_sent === true &&
        item?.first_aligner_change_followed !== true,
    },

    {
      id: 'clinical_return',
      label:
        item?.has_scheduled_return === true
          ? 'Retorno agendado'
          : 'Agendar retorno',
      completed:
        item?.has_scheduled_return === true,
      active:
        item?.first_aligner_change_followed === true &&
        item?.has_scheduled_return !== true,
    },

    {
      id: 'next_set',
      label: allAlignersDelivered
        ? 'Todos entregues ao paciente'
        : nextSetReceived
        ? 'Lote recebido na clínica'
        : nextSetConfigured
        ? 'Aguardando lote'
        : 'Próximo conjunto',
      completed:
        allAlignersDelivered || (
          hasCurrentSet &&
          !nextSetConfigured &&
          completed
        ),
      active:
        !allAlignersDelivered && (
        nextSetReceived ||
        nextSetConfigured ||
        (
          item?.has_scheduled_return === true &&
          !completed
        )),
      attention: !allAlignersDelivered && nextSetReceived,
    },

    {
      id: 'completion',
      label: completed
        ? 'Tratamento concluído'
        : 'Conclusão',
      completed,
      active:
        !completed &&
        treatmentStatus === 'in_progress' &&
        !nextSetConfigured,
    },
  ];
};

const getTreatmentHealthDiagnosis = (item) => {
  const treatmentStatus =
    item?.status ||
    item?.treatment_status;

  const monitoringStatus =
    item?.monitoring_status;

  const currentStage =
    getOperationalCurrentStage(item);

  const reviewDays =
    differenceInCalendarDays(
      item?.next_recommended_review_date
    );

  const nextSetWaitingDelivery =
    !hasAllAlignersDelivered(item) &&
    item?.next_set_received === true &&
    item?.next_set_start_stage !== null &&
    item?.next_set_start_stage !== undefined;

  /*
   * Situações que não devem receber score operacional.
   */

  if (
    treatmentStatus === 'completed' ||
    treatmentStatus === 'cancelled'
  ) {
    return {
      level: 'closed',
      score: null,
      scoreLabel: 'Encerrado',
      reasons: [
        treatmentStatus === 'completed'
          ? 'Tratamento marcado como concluído.'
          : 'Tratamento marcado como cancelado.',
      ],
      deductions: [],
    };
  }

  if (treatmentStatus === 'paused') {
    return {
      level: 'paused',
      score: null,
      scoreLabel: 'Pausado',
      reasons: [
        'O tratamento está pausado e não entra na avaliação normal de prazos.',
      ],
      deductions: [],
    };
  }

  if (treatmentStatus === 'planned') {
    const preparationReasons = [];

    if (nextSetWaitingDelivery) {
      preparationReasons.push(
        'Há um conjunto recebido na clínica.'
      );
    }

    if (
      item?.initial_guidance_completed !== true
    ) {
      preparationReasons.push(
        'As orientações iniciais ainda não foram registradas.'
      );
    }

    return {
      level: 'preparation',
      score: null,
      scoreLabel: 'Em preparação',
      reasons:
        preparationReasons.length > 0
          ? preparationReasons.slice(0, 3)
          : [
              'Tratamento planejado, aguardando o início do acompanhamento.',
            ],
      deductions: [],
    };
  }

  let score = 100;

  const reasons = [];
  const deductions = [];

  const addDeduction = (
    points,
    reason,
    code
  ) => {
    score -= points;

    reasons.push(reason);

    deductions.push({
      code,
      points,
      reason,
    });
  };

  /*
   * Prazo clínico e estado do monitoramento.
   */

  if (monitoringStatus === 'urgent') {
    addDeduction(
      40,
      reviewDays !== null &&
        reviewDays < 0
        ? `A revisão recomendada está atrasada há ${Math.abs(
            reviewDays
          )} ${
            Math.abs(reviewDays) === 1
              ? 'dia'
              : 'dias'
          }.`
        : 'O acompanhamento ultrapassou o limite configurado para intervenção urgente.',
      'urgent_monitoring'
    );
  } else if (
    monitoringStatus === 'overdue'
  ) {
    addDeduction(
      25,
      reviewDays !== null &&
        reviewDays < 0
        ? `A revisão clínica está vencida há ${Math.abs(
            reviewDays
          )} ${
            Math.abs(reviewDays) === 1
              ? 'dia'
              : 'dias'
          }.`
        : 'A revisão clínica recomendada está vencida.',
      'overdue_monitoring'
    );
  } else if (
    monitoringStatus === 'attention'
  ) {
    addDeduction(
      12,
      reviewDays === 0
        ? 'A próxima revisão clínica está prevista para hoje.'
        : reviewDays === 1
        ? 'A próxima revisão clínica está prevista para amanhã.'
        : 'O prazo da próxima revisão clínica está próximo.',
      'attention_monitoring'
    );
  } else if (
    monitoringStatus === 'needs_review'
  ) {
    addDeduction(
      15,
      'Os dados do acompanhamento precisam ser revisados.',
      'needs_review'
    );
  }

  /*
   * Ausência de uma referência clínica confiável.
   */

  if (
    monitoringStatus === 'no_reference' ||
    !item?.monitoring_reference_date
  ) {
    addDeduction(
      20,
      'Não há uma referência clínica confiável para calcular o acompanhamento.',
      'no_reference'
    );
  }

  /*
   * Ausência de retorno futuro.
   */

  if (
    !item?.has_scheduled_return &&
    ['urgent', 'overdue'].includes(
      monitoringStatus
    )
  ) {
    addDeduction(
      20,
      'O acompanhamento está atrasado e não existe retorno futuro agendado.',
      'delayed_without_return'
    );
  } else if (
    !item?.has_scheduled_return &&
    monitoringStatus === 'attention'
  ) {
    addDeduction(
      8,
      'O prazo está próximo e ainda não existe retorno futuro agendado.',
      'attention_without_return'
    );
  }

  /*
   * Ações iniciais.
   *
   * Só são penalizadas quando o tratamento já começou.
   */

  if (
    item?.phase_status === 'active_delivery' &&
    currentStage > 0 &&
    item?.initial_guidance_completed !== true
  ) {
    addDeduction(
      8,
      'O tratamento já começou, mas as orientações iniciais ainda não foram registradas.',
      'initial_guidance_pending'
    );
  }

  if (
    item?.phase_status === 'active_delivery' &&
    currentStage >= 2 &&
    item?.adaptation_message_sent !== true
  ) {
    addDeduction(
      6,
      'O acompanhamento da adaptação ainda não foi registrado.',
      'adaptation_pending'
    );
  }

  if (
    item?.phase_status === 'active_delivery' &&
    currentStage >= 2 &&
    item?.first_aligner_change_followed !== true
  ) {
    addDeduction(
      6,
      'A primeira troca de alinhador ainda não foi acompanhada.',
      'first_change_pending'
    );
  }

  /*
   * Garantia de faixa válida.
   */

  score = Math.max(
    0,
    Math.min(100, score)
  );

  let level = 'healthy';

  if (score < 60) {
    level = 'critical';
  } else if (score < 85) {
    level = 'attention';
  }

  /*
   * Situações de maior risco prevalecem mesmo quando
   * o score permanece próximo do limite.
   */

  if (
    monitoringStatus === 'urgent' ||
    (
      ['urgent', 'overdue'].includes(
        monitoringStatus
      ) &&
      !item?.has_scheduled_return
    )
  ) {
    level = 'critical';
  }

  if (reasons.length === 0) {
    reasons.push(
      item?.has_scheduled_return
        ? 'Acompanhamento dentro do prazo e com retorno futuro registrado.'
        : 'Acompanhamento dentro do prazo configurado.'
    );
  }

  return {
    level,
    score,
    scoreLabel: `${score} de 100`,
    reasons: [...new Set(reasons)].slice(0, 3),
    deductions,
  };
};

const onlyDigits = (value) =>
  String(value || '').replace(/\D/g, '');

const getPatientFirstName = (name) => {
  const normalized = String(name || '').trim();

  if (!normalized) {
    return 'paciente';
  }

  return normalized.split(/\s+/)[0];
};

const buildWhatsAppTemplates = (item) => {
  const firstName = getPatientFirstName(
    item?.patient_name
  );

  const professionalName =
    item?.professional_name ||
    'seu profissional responsável';

  const templates = [
    {
      id: 'initial_guidance',
      title: 'Orientações iniciais',
      description:
        'Reforça os principais cuidados no início do tratamento.',
      monitoringField:
        'initial_guidance_completed',
      monitoringLabel:
        'Marcar orientações iniciais como realizadas',
      message:
        `Olá, ${firstName}! Tudo bem?\n\n` +
        `Gostaríamos de reforçar algumas orientações importantes para o seu tratamento com alinhadores:\n\n` +
        `• utilize os alinhadores pelo período diário recomendado;\n` +
        `• retire-os para alimentação e higienização;\n` +
        `• higienize os dentes antes de recolocá-los;\n` +
        `• mantenha os alinhadores guardados no estojo quando não estiverem em uso;\n` +
        `• siga corretamente as datas de troca orientadas pelo profissional.\n\n` +
        `Caso tenha qualquer dúvida ou dificuldade, entre em contato conosco.`,
    },
    {
      id: 'schedule_return',
      title: 'Solicitar agendamento de retorno',
      description:
        'Convida o paciente a entrar em contato para marcar o próximo acompanhamento.',
      message:
        `Olá, ${firstName}! Tudo bem?\n\n` +
        `Estamos entrando em contato para organizar seu próximo retorno do tratamento com alinhadores.\n\n` +
        `Por favor, fale conosco para verificarmos os horários disponíveis.\n\n` +
        `Profissional responsável: ${professionalName}.`,
    },
    {
      id: 'follow_up',
      title: 'Acompanhamento da adaptação',
      description:
        'Pergunta como está a adaptação e o uso dos alinhadores.',
      monitoringField:
        'adaptation_message_sent',
      monitoringLabel:
        'Marcar mensagem de adaptação como enviada',
      message:
        `Olá, ${firstName}! Tudo bem?\n\n` +
        `Gostaríamos de saber como está sua adaptação e o uso dos alinhadores.\n\n` +
        `Você está conseguindo utilizar conforme as orientações? Teve alguma dificuldade, desconforto ou problema com algum alinhador?\n\n` +
        `Caso precise, estamos à disposição.`,
    },
    {
      id: 'first_change',
      title: 'Acompanhar primeira troca',
      description:
        'Mensagem específica para o primeiro período de troca.',
      monitoringField:
        'first_aligner_change_followed',
      monitoringLabel:
        'Marcar primeira troca como acompanhada',
      message:
        `Olá, ${firstName}! Tudo bem?\n\n` +
        `Estamos passando para acompanhar sua primeira troca de alinhador.\n\n` +
        `A troca ocorreu normalmente? O novo alinhador está encaixando corretamente?\n\n` +
        `Caso perceba alguma dificuldade, entre em contato conosco.`,
    },
  ];

  if (item?.has_scheduled_return) {
    const appointmentDate =
      item?.next_appointment?.appointment_date;

    templates.unshift({
      id: 'scheduled_return',
      title: 'Lembrar retorno agendado',
      description:
        'Reforça a existência do próximo retorno.',
      message:
        `Olá, ${firstName}! Tudo bem?\n\n` +
        `Passando para lembrar que seu retorno do tratamento com alinhadores está agendado${
          appointmentDate
            ? ` para ${formatDateBR(appointmentDate)}`
            : ''
        }.\n\n` +
        `Caso precise confirmar alguma informação ou solicitar alteração, entre em contato conosco.`,
    });
  }

  return templates;
};

const getRecommendedWhatsAppTemplateId = (
  item,
  templates
) => {
  const availableTemplateIds = new Set(
    templates.map((template) => template.id)
  );

  const canUse = (templateId) =>
    availableTemplateIds.has(templateId);

  if (['awaiting_documents', 'awaiting_aligners'].includes(item?.phase_status)) {
    return '';
  }

  if (
    item?.next_follow_up_date &&
    item?.monitoring_status === 'on_time'
  ) {
    return '';
  }

  if (
    !item?.has_scheduled_return &&
    ['urgent', 'overdue', 'attention'].includes(
      item?.monitoring_status
    ) &&
    canUse('schedule_return')
  ) {
    return 'schedule_return';
  }

  if (
    item?.phase_status === 'active_delivery' &&
    item?.initial_guidance_completed !== true &&
    canUse('initial_guidance')
  ) {
    return 'initial_guidance';
  }

  if (
    item?.phase_status === 'active_delivery' &&
    item?.adaptation_message_sent !== true &&
    canUse('follow_up')
  ) {
    return 'follow_up';
  }

  if (
    item?.phase_status === 'active_delivery' &&
    item?.first_aligner_change_followed !== true &&
    canUse('first_change')
  ) {
    return 'first_change';
  }

  if (
    item?.has_scheduled_return &&
    canUse('scheduled_return')
  ) {
    return 'scheduled_return';
  }

  if (canUse('schedule_return')) {
    return 'schedule_return';
  }

  const firstTemplateWithoutCompletedAction =
    templates.find((template) => {
      if (!template.monitoringField) {
        return true;
      }

      return (
        item?.[template.monitoringField] !== true
      );
    });

  return (
    firstTemplateWithoutCompletedAction?.id ||
    templates[0]?.id ||
    ''
  );
};

const getRecommendedActionSummary = (item) => {
  if (item?.phase_status === 'awaiting_aligners') {
    return {
      templateId: null,
      title: 'Aguardando alinhadores',
      description: item?.next_follow_up_date
        ? `Conferir novamente a chegada em ${formatDateBR(
            item.next_follow_up_date
          )}.`
        : 'Os primeiros alinhadores ainda não chegaram à clínica.',
      tone: 'blue',
    };
  }

  if (item?.phase_status === 'awaiting_documents') {
    return {
      templateId: null,
      title: 'Aguardando documentação/exames',
      description: item?.next_follow_up_date
        ? `Falar novamente com o paciente em ${formatDateBR(
            item.next_follow_up_date
          )}.`
        : 'O tratamento ainda não foi iniciado. Defina uma data de contato quando necessário.',
      tone: 'blue',
    };
  }
  if (item?.monitoring_status === 'needs_review') {
    return {
      templateId: null,
      title: 'Revisar acompanhamento',
      description:
        item?.monitoring_reference_source === 'treatment_start'
          ? 'Ainda não há consulta concluída nem uma data definida para o próximo acompanhamento.'
          : 'Falta uma data confiável para calcular o próximo acompanhamento.',
      tone: 'gray',
    };
  }
  if (
    item?.next_follow_up_date &&
    item?.monitoring_status === 'on_time'
  ) {
    return {
      templateId: null,
      title: 'Acompanhamento em dia',
      description: `Falar novamente com o paciente em ${formatDateBR(
        item.next_follow_up_date
      )}.`,
      tone: 'green',
    };
  }
  if (item?.phase_status === 'awaiting_refinement') {
    return {
      templateId: null,
      title: 'Acompanhar produção do refinamento',
      description: item?.next_follow_up_date
        ? `Revisar a chegada dos novos alinhadores até ${formatDateBR(
            item.next_follow_up_date
          )}.`
        : 'O caso foi reescaneado e aguarda a produção dos novos alinhadores.',
      tone: 'blue',
    };
  }

  if (item?.phase_status === 'refinement_active') {
    return {
      templateId: item?.has_scheduled_return
        ? 'scheduled_return'
        : 'schedule_return',
      title: item?.has_scheduled_return
        ? 'Acompanhar refinamento'
        : 'Programar retorno do refinamento',
      description: item?.has_scheduled_return
        ? `Há um retorno previsto para ${formatDateBR(
            item?.next_appointment?.appointment_date
          )}.`
        : item?.next_follow_up_date
        ? `Entrar em contato até ${formatDateBR(
            item.next_follow_up_date
          )} para acompanhar o refinamento.`
        : 'O paciente está recebendo novos alinhadores e ainda não possui retorno futuro na agenda.',
      tone: item?.has_scheduled_return ? 'green' : 'amber',
    };
  }

  if (item?.phase_status === 'final_follow_up') {
    return {
      templateId: 'schedule_return',
      title: item?.has_scheduled_return
        ? 'Realizar consulta final'
        : 'Organizar consulta final',
      description: item?.has_scheduled_return
        ? `Há uma consulta prevista para ${formatDateBR(
            item?.next_appointment?.appointment_date
          )}. Depois dela, registre o acompanhamento e conclua o tratamento.`
        : item?.next_follow_up_date
        ? `Entrar em contato até ${formatDateBR(
            item.next_follow_up_date
          )} para organizar a consulta final.`
        : 'O paciente não possui mais alinhadores para receber. Registre ou agende a consulta final.',
      tone: item?.has_scheduled_return ? 'green' : 'amber',
    };
  }

  if (item?.phase_status === 'awaiting_retention') {
    return {
      templateId: item?.has_scheduled_return
        ? 'scheduled_return'
        : 'schedule_return',
      title: item?.has_scheduled_return
        ? 'Acompanhar instalação da contenção'
        : 'Organizar instalação da contenção',
      description: item?.has_scheduled_return
        ? `Há uma consulta prevista para ${formatDateBR(
            item?.next_appointment?.appointment_date
          )}.`
        : item?.next_follow_up_date
        ? `Entrar em contato até ${formatDateBR(
            item.next_follow_up_date
          )} para organizar a contenção.`
        : 'O tratamento com alinhadores terminou e o paciente aguarda a contenção.',
      tone: item?.has_scheduled_return ? 'green' : 'amber',
    };
  }

  if (item?.status === 'completed') {
    return {
      templateId: null,
      title: 'Tratamento concluído',
      description: 'Nenhuma ação pendente para este acompanhamento.',
      tone: 'gray',
    };
  }

  if (item?.status === 'paused' || item?.status === 'cancelled') {
    return {
      templateId: null,
      title: item?.status === 'paused'
        ? 'Tratamento pausado'
        : 'Tratamento cancelado',
      description: 'Altere a situação atual quando o acompanhamento for retomado.',
      tone: 'gray',
    };
  }

  if (
    !item?.next_follow_up_date &&
    !item?.has_scheduled_return
  ) {
    return {
      templateId: null,
      title: 'Definir próximo contato',
      description: 'Informe quando deseja falar novamente com o paciente.',
      tone: 'gray',
    };
  }

  const templates = buildWhatsAppTemplates(item);

  const recommendedTemplateId =
    getRecommendedWhatsAppTemplateId(
      item,
      templates
    );

  const recommendedTemplate = templates.find(
    (template) =>
      template.id === recommendedTemplateId
  );

  const defaultSummary = {
    templateId: recommendedTemplateId,
    title:
      recommendedTemplate?.title ||
      'Revisar acompanhamento',
    description:
      'Revise os dados do tratamento e defina a próxima ação.',
    tone: 'gray',
  };

  switch (recommendedTemplateId) {
    case 'schedule_return':
      return {
        templateId: recommendedTemplateId,
        title: 'Falar com o paciente',
        description:
          item?.monitoring_status === 'urgent'
            ? 'A data definida para contato está bastante atrasada.'
            : item?.monitoring_status === 'overdue'
            ? 'A data definida para contato já passou.'
            : item?.monitoring_status === 'attention'
            ? 'A data definida para contato está próxima.'
            : 'Revise se já é o momento de falar com o paciente.',
        tone:
          item?.monitoring_status === 'urgent'
            ? 'red'
            : item?.monitoring_status === 'overdue'
            ? 'orange'
            : 'amber',
      };

    case 'initial_guidance':
      return {
        templateId: recommendedTemplateId,
        title: 'Enviar orientações iniciais',
        description:
          'As orientações iniciais do tratamento ainda não foram registradas.',
        tone: 'amber',
      };

    case 'follow_up':
      return {
        templateId: recommendedTemplateId,
        title: 'Acompanhar adaptação',
        description:
          'Verifique como o paciente está se adaptando ao uso dos alinhadores.',
        tone: 'amber',
      };

    case 'first_change':
      return {
        templateId: recommendedTemplateId,
        title: 'Acompanhar primeira troca',
        description:
          'A primeira troca de alinhador ainda precisa ser acompanhada.',
        tone: 'amber',
      };

    case 'scheduled_return':
      return {
        templateId: recommendedTemplateId,
        title: 'Reforçar retorno agendado',
        description: item?.next_appointment
          ?.appointment_date
          ? `O próximo retorno está agendado para ${formatDateBR(
              item.next_appointment.appointment_date
            )}.`
          : 'O paciente possui um retorno futuro registrado.',
        tone: 'green',
      };

    default:
      return defaultSummary;
  }
};

function getOperationalCurrentStage(treatment) {
  const deliveredSetEnd = Number(
    treatment?.current_set_end_stage
  );

  if (
    Number.isFinite(deliveredSetEnd) &&
    deliveredSetEnd > 0
  ) {
    return deliveredSetEnd;
  }

  return Number(treatment?.current_stage || 0);
}

const calculateProgress = (treatment) => {
  const total = Number(treatment?.total_stages || 0);

  const current =
    getOperationalCurrentStage(treatment);

  if (!total) {
    return 0;
  }

  return Math.min(
    100,
    Math.max(0, Math.round((current / total) * 100))
  );
};

const numericOrNull = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

const getApiErrorMessage = (
  error,
  fallback = 'Não foi possível concluir a operação.'
) => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg || item?.message)
      .filter(Boolean);

    if (messages.length) {
      return messages.join(' ');
    }
  }

  if (
    detail &&
    typeof detail === 'object' &&
    typeof detail.message === 'string'
  ) {
    return detail.message;
  }

  return fallback;
};

function TreatmentOperationalTimeline({ item }) {
  const steps = getTreatmentTimelineSteps(item);

  return (
    <div className="mt-5 rounded-xl border border-gray-200 bg-white/70 p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-gray-800">
            Etapas operacionais
          </p>

          <p className="mt-0.5 text-xs text-gray-500">
            Visão resumida do fluxo atual do tratamento.
          </p>
        </div>

        <span className="text-xs font-medium text-gray-500">
          {
            steps.filter(
              (step) => step.completed
            ).length
          }{' '}
          de {steps.length} concluídas
        </span>
      </div>

      <div className="overflow-x-auto pb-1">
        <div className="flex min-w-[680px] items-start">
          {steps.map((step, index) => {
            const isLast =
              index === steps.length - 1;

            const circleClass = step.completed
              ? 'border-green-600 bg-green-600 text-white'
              : step.attention
              ? 'border-blue-600 bg-blue-100 text-blue-700'
              : step.active
              ? 'border-[var(--clinic-primary)] bg-white text-[var(--clinic-primary)]'
              : 'border-gray-300 bg-gray-100 text-gray-400';

            const lineClass = step.completed
              ? 'bg-green-500'
              : 'bg-gray-200';

            const labelClass = step.completed
              ? 'text-green-800'
              : step.attention
              ? 'font-semibold text-blue-800'
              : step.active
              ? 'font-semibold text-gray-800'
              : 'text-gray-500';

            return (
              <React.Fragment key={step.id}>
                <div className="flex w-[96px] shrink-0 flex-col items-center text-center">
                  <span
                    className={`
                      flex
                      h-8
                      w-8
                      items-center
                      justify-center
                      rounded-full
                      border-2
                      text-xs
                      font-bold
                      ${circleClass}
                    `}
                  >
                    {step.completed ? (
                      <CheckCircle2 size={17} />
                    ) : step.attention ? (
                      <AlertCircle size={16} />
                    ) : step.active ? (
                      index + 1
                    ) : (
                      index + 1
                    )}
                  </span>

                  <span
                    className={`
                      mt-2
                      text-[11px]
                      leading-tight
                      ${labelClass}
                    `}
                  >
                    {step.label}
                  </span>
                </div>

                {!isLast && (
                  <div
                    className={`
                      mt-[15px]
                      h-0.5
                      min-w-[18px]
                      flex-1
                      ${lineClass}
                    `}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 border-t border-gray-100 pt-3 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-green-600" />
          Concluída
        </span>

        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border-2 border-[var(--clinic-primary)] bg-white" />
          Etapa atual
        </span>

        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
          Requer ação
        </span>

        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
          Pendente
        </span>
      </div>
    </div>
  );
}

function TreatmentHealthDiagnosis({ item }) {
  const diagnosis =
    getTreatmentHealthDiagnosis(item);

  const config =
    TREATMENT_HEALTH_CONFIG[
      diagnosis.level
    ] ||
    TREATMENT_HEALTH_CONFIG.attention;

  const HealthIcon = config.icon;

  return (
    <div
      className={`
        mt-4
        rounded-xl
        border
        p-3
        ${config.containerClass}
      `}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <span
          className={`
            flex
            h-10
            w-10
            shrink-0
            items-center
            justify-center
            rounded-xl
            ${config.iconClass}
          `}
        >
          <HealthIcon size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p
                className={`
                  text-[11px]
                  font-bold
                  uppercase
                  tracking-wide
                  ${config.textClass}
                `}
              >
                Saúde do acompanhamento
              </p>

              <div className="mt-1 flex flex-wrap items-center gap-2">
                <p
                  className={`
                    font-semibold
                    ${config.titleClass}
                  `}
                >
                  {config.label}
                </p>

                <span
                  className={`
                    inline-flex
                    rounded-full
                    px-2.5
                    py-1
                    text-[11px]
                    font-semibold
                    ${config.badgeClass}
                  `}
                >
                  Diagnóstico automático
                </span>
              </div>
            </div>

            <div className="shrink-0 text-right">
              {diagnosis.score !== null ? (
                <>
                  <p
                    className={`
                      text-3xl
                      font-bold
                      leading-none
                      ${config.titleClass}
                    `}
                  >
                    {diagnosis.score}
                  </p>

                  <p
                    className={`
                      mt-1
                      text-[10px]
                      font-semibold
                      uppercase
                      tracking-wide
                      ${config.textClass}
                    `}
                  >
                    de 100 pontos
                  </p>
                </>
              ) : (
                <span
                  className={`
                    inline-flex
                    rounded-full
                    px-3
                    py-1.5
                    text-xs
                    font-semibold
                    ${config.badgeClass}
                  `}
                >
                  Não avaliado
                </span>
              )}
            </div>
          </div>

          {diagnosis.score !== null && (
            <div className="mt-3">
              <div className="h-2 overflow-hidden rounded-full bg-white/80">
                <div
                  className={`
                    h-full
                    rounded-full
                    transition-all
                    ${
                      diagnosis.level ===
                      'healthy'
                        ? 'bg-green-600'
                        : diagnosis.level ===
                          'critical'
                        ? 'bg-red-600'
                        : 'bg-amber-500'
                    }
                  `}
                  style={{
                    width: `${diagnosis.score}%`,
                  }}
                />
              </div>

              <div
                className={`
                  mt-1.5
                  flex
                  items-center
                  justify-between
                  text-[10px]
                  font-medium
                  ${config.textClass}
                `}
              >
                <span>Crítico</span>
                <span>Atenção</span>
                <span>Saudável</span>
              </div>
            </div>
          )}

          <ul
            className={`
              mt-3
              space-y-1.5
              text-xs
              leading-relaxed
              ${config.textClass}
            `}
          >
            {diagnosis.reasons.map(
              (reason, index) => (
                <li
                  key={`${reason}-${index}`}
                  className="flex items-start gap-2"
                >
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-current" />

                  <span>{reason}</span>
                </li>
              )
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

const buildMonitoringForm = (item) => ({
  phase_status: getSimpleSituation(item),

  total_stages: item?.total_stages || '',

  next_follow_up_date:
    item?.next_follow_up_date || '',

  follow_up_interval_mode:
    item?.follow_up_interval_mode || 'by_aligners',

  wear_days_per_aligner:
    Number(item?.wear_days_per_aligner || 7),

  follow_up_aligners_count:
    Number(item?.follow_up_aligners_count || 6),

  follow_up_interval_days:
    Number(item?.follow_up_interval_days || 90),

  attention_days_before:
    Number(item?.attention_days_before ?? 7),

  urgent_after_days:
    Number(item?.urgent_after_days || 30),

  manual_last_clinical_visit_date:
    item?.manual_last_clinical_visit_date || '',

  current_set_start_stage:
    item?.current_set_start_stage ?? '',

  current_set_end_stage:
    item?.current_set_end_stage ?? '',

  current_set_delivered_at:
    item?.current_set_delivered_at || '',

  next_set_start_stage:
    item?.next_set_start_stage ?? '',

  next_set_end_stage:
    item?.next_set_end_stage ?? '',

  next_set_received:
    item?.next_set_received === true,

  next_set_received_at:
    item?.next_set_received_at || '',

  all_aligners_delivered:
    hasAllAlignersDelivered(item),

  initial_guidance_completed:
    item?.initial_guidance_completed === true,

  adaptation_message_sent:
    item?.adaptation_message_sent === true,

  first_aligner_change_followed:
    item?.first_aligner_change_followed === true,
});

function SummaryCard({
  title,
  value,
  status,
  active,
  onClick,
}) {
  const config =
    MONITORING_CONFIG[status] ||
    MONITORING_CONFIG.no_reference;

  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        rounded-2xl
        border
        p-4
        text-left
        shadow-sm
        transition
        hover:-translate-y-0.5
        hover:shadow-md
        ${config.cardClass}
        ${
          active
            ? 'ring-2 ring-[var(--clinic-primary)] ring-offset-2'
            : ''
        }
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-600">
            {title}
          </p>

          <p className="mt-2 text-3xl font-bold text-gray-900">
            {value || 0}
          </p>

          <p className="mt-1 text-[11px] leading-tight text-gray-500">
            {config.description}
          </p>
        </div>

        <span
          className={`
            rounded-xl
            p-2
            ${config.badgeClass}
          `}
        >
          <Icon size={21} />
        </span>
      </div>
    </button>
  );
}

function WhatsAppMessageModal({
  item,
  onClose,
  onUpdated,
  toast,
}) {
  const templates = useMemo(
    () => buildWhatsAppTemplates(item),
    [item]
  );

  const recommendedTemplateId = useMemo(
    () =>
      getRecommendedWhatsAppTemplateId(
        item,
        templates
      ),
    [item, templates]
  );

  const orderedTemplates = useMemo(() => {
    return [...templates].sort((a, b) => {
      if (a.id === recommendedTemplateId) {
        return -1;
      }

      if (b.id === recommendedTemplateId) {
        return 1;
      }

      const aCompleted =
        a.monitoringField &&
        item?.[a.monitoringField] === true;

      const bCompleted =
        b.monitoringField &&
        item?.[b.monitoringField] === true;

      if (aCompleted !== bCompleted) {
        return aCompleted ? 1 : -1;
      }

      return 0;
    });
  }, [templates, recommendedTemplateId, item]);

  const [selectedTemplateId, setSelectedTemplateId] =
    useState(recommendedTemplateId);

  const [customMessage, setCustomMessage] =
    useState('');

  const [
    registerMonitoringAction,
    setRegisterMonitoringAction,
  ] = useState(false);

  const [updatingMonitoring, setUpdatingMonitoring] =
    useState(false);

  const [
    showAlreadyCompletedWarning,
    setShowAlreadyCompletedWarning,
  ] = useState(false);

  const selectedTemplate = templates.find(
    (template) =>
      template.id === selectedTemplateId
  );

  const monitoringField =
    selectedTemplate?.monitoringField || null;

  const monitoringAlreadyCompleted =
    monitoringField
      ? item?.[monitoringField] === true
      : false;

  const message =
    selectedTemplateId === 'custom'
      ? customMessage
      : selectedTemplate?.message || '';

  useEffect(() => {
    setRegisterMonitoringAction(false);
    setShowAlreadyCompletedWarning(false);
  }, [selectedTemplateId]);

  useEffect(() => {
    setSelectedTemplateId(recommendedTemplateId);
  }, [recommendedTemplateId]);

  const registerSelectedMonitoringAction =
    async () => {
      if (
        !monitoringField ||
        monitoringAlreadyCompleted ||
        !registerMonitoringAction
      ) {
        return;
      }

      setUpdatingMonitoring(true);

      try {
        await axios.patch(
          `${API_URL}/aligner-treatments/${item.id}/monitoring`,
          {
            [monitoringField]: true,
          },
          authConfig()
        );

        if (typeof onUpdated === 'function') {
          await onUpdated();
        }

        toast?.success?.(
          'Ação de acompanhamento registrada.'
        );
      } catch (error) {
        console.error(
          'Erro ao registrar ação do acompanhamento:',
          error
        );

        toast?.error?.(
          getApiErrorMessage(
            error,
            'O WhatsApp será aberto, mas não foi possível registrar a ação.'
          )
        );
      } finally {
        setUpdatingMonitoring(false);
      }
    };

  const openWhatsAppAndRegister = async () => {
    let phone = onlyDigits(item?.patient_phone);

    if (!phone) {
      toast?.error?.(
        'Paciente sem telefone cadastrado.'
      );
      return;
    }

    if (!message.trim()) {
      toast?.error?.(
        'Selecione ou escreva uma mensagem.'
      );
      return;
    }

    if (!phone.startsWith('55')) {
      phone = `55${phone}`;
    }

    const url =
      `https://wa.me/${phone}` +
      `?text=${encodeURIComponent(message.trim())}`;

    window.open(
      url,
      '_blank',
      'noopener,noreferrer'
    );

    await registerSelectedMonitoringAction();

    onClose();
  };

  const handleOpenWhatsApp = async () => {
    if (
      monitoringField &&
      monitoringAlreadyCompleted
    ) {
      setShowAlreadyCompletedWarning(true);
      return;
    }

    await openWhatsAppAndRegister();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="
          max-h-[92vh]
          w-full
          max-w-2xl
          overflow-y-auto
          rounded-2xl
          bg-white
          shadow-2xl
        "
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Enviar mensagem pelo WhatsApp
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              {item.patient_name}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <section>
            <h3 className="mb-3 font-semibold text-gray-900">
              Escolha a mensagem
            </h3>

            <div className="mb-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs leading-relaxed text-blue-800">
              O OdontoAssist posicionou primeiro a mensagem
              mais adequada para a situação atual do
              tratamento. As mensagens vinculadas a ações já
              registradas aparecem ao final da lista e exigem
              confirmação antes do reenvio.
            </div>

            <div className="space-y-2">
              {orderedTemplates.map((template) => {
                const templateHasMonitoringAction =
                  Boolean(template.monitoringField);

                const templateActionCompleted =
                  templateHasMonitoringAction &&
                  item?.[template.monitoringField] === true;

                return (
                <label
                  key={template.id}
                  className={`
                    flex
                    cursor-pointer
                    items-start
                    gap-3
                    rounded-xl
                    border
                    p-3
                    transition
                    ${
                      selectedTemplateId ===
                      template.id
                        ? 'border-[var(--clinic-primary)] bg-blue-50'
                        : templateActionCompleted
                        ? 'border-green-200 bg-green-50/40 hover:bg-green-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="whatsapp-template"
                    value={template.id}
                    checked={
                      selectedTemplateId ===
                      template.id
                    }
                    onChange={() =>
                      setSelectedTemplateId(
                        template.id
                      )
                    }
                    className="mt-1"
                  />

                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-800">
                        {template.title}
                      </span>

                      {template.id ===
                        recommendedTemplateId && (
                        <span
                          className="
                            rounded-full
                            bg-[var(--clinic-primary)]
                            px-2
                            py-0.5
                            text-[10px]
                            font-bold
                            uppercase
                            tracking-wide
                            text-white
                          "
                        >
                          Recomendada
                        </span>
                      )}

                      {templateHasMonitoringAction &&
                        !templateActionCompleted && (
                          <span
                            className="
                              rounded-full
                              bg-amber-100
                              px-2
                              py-0.5
                              text-[10px]
                              font-bold
                              uppercase
                              tracking-wide
                              text-amber-800
                            "
                          >
                            Ação pendente
                          </span>
                        )}

                      {templateActionCompleted && (
                        <span
                          className="
                            inline-flex
                            items-center
                            gap-1
                            rounded-full
                            bg-green-100
                            px-2
                            py-0.5
                            text-[10px]
                            font-bold
                            uppercase
                            tracking-wide
                            text-green-800
                          "
                        >
                          <CheckCircle2 size={11} />

                          Já registrada
                        </span>
                      )}
                    </span>

                    <span className="mt-1 block text-xs text-gray-500">
                      {template.description}
                    </span>
                  </span>
                </label>
                );
              })}

              <label
                className={`
                  flex
                  cursor-pointer
                  items-start
                  gap-3
                  rounded-xl
                  border
                  p-3
                  transition
                  ${
                    selectedTemplateId === 'custom'
                      ? 'border-[var(--clinic-primary)] bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="whatsapp-template"
                  value="custom"
                  checked={
                    selectedTemplateId === 'custom'
                  }
                  onChange={() =>
                    setSelectedTemplateId('custom')
                  }
                  className="mt-1"
                />

                <span>
                  <span className="block text-sm font-semibold text-gray-800">
                    Mensagem personalizada
                  </span>

                  <span className="mt-1 block text-xs text-gray-500">
                    Escreva livremente o texto que será
                    aberto no WhatsApp.
                  </span>
                </span>
              </label>
            </div>
          </section>

          {selectedTemplateId === 'custom' ? (
            <section>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-gray-800">
                  Mensagem
                </span>

                <textarea
                  rows={8}
                  value={customMessage}
                  onChange={(event) =>
                    setCustomMessage(
                      event.target.value
                    )
                  }
                  placeholder="Digite a mensagem..."
                  className="
                    w-full
                    resize-y
                    rounded-xl
                    border
                    border-gray-300
                    px-3
                    py-3
                    outline-none
                    focus:border-[var(--clinic-primary)]
                  "
                />
              </label>
            </section>
          ) : (
            <>
              <section>
                <p className="mb-2 text-sm font-semibold text-gray-800">
                  Pré-visualização
                </p>

                <div className="whitespace-pre-wrap rounded-xl border border-green-200 bg-green-50 p-4 text-sm leading-relaxed text-gray-700">
                  {message}
                </div>
              </section>
            
              {monitoringField && (
                <section>
                  {monitoringAlreadyCompleted ? (
                    <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
                      <CheckCircle2
                        size={19}
                        className="mt-0.5 shrink-0 text-green-700"
                      />

                      <div>
                        <p className="text-sm font-semibold text-green-900">
                          Ação já registrada
                        </p>

                        <p className="mt-1 text-xs text-green-700">
                          Esta etapa já consta como concluída no
                          acompanhamento do paciente.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                      <input
                        type="checkbox"
                        checked={
                          registerMonitoringAction
                        }
                        onChange={(event) =>
                          setRegisterMonitoringAction(
                            event.target.checked
                          )
                        }
                        className="mt-0.5 h-4 w-4"
                      />

                      <span>
                        <span className="block text-sm font-semibold text-blue-900">
                          {
                            selectedTemplate.monitoringLabel
                          }
                        </span>

                        <span className="mt-1 block text-xs text-blue-700">
                          O registro será atualizado quando o
                          WhatsApp for aberto.
                        </span>
                      </span>
                    </label>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        {showAlreadyCompletedWarning && (
          <div className="mx-5 mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle
                size={20}
                className="mt-0.5 shrink-0 text-amber-700"
              />

              <div className="flex-1">
                <p className="font-semibold text-amber-900">
                  Esta ação já foi registrada
                </p>

                <p className="mt-1 text-sm text-amber-800">
                  Esta etapa já consta como concluída no
                  acompanhamento do paciente. Deseja abrir o
                  WhatsApp e enviar novamente esta mensagem?
                </p>

                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setShowAlreadyCompletedWarning(false)
                    }
                    className="
                      rounded-xl
                      border
                      border-amber-300
                      bg-white
                      px-4
                      py-2
                      text-sm
                      font-semibold
                      text-amber-900
                      hover:bg-amber-100
                    "
                  >
                    Não, voltar
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      setShowAlreadyCompletedWarning(false);
                      await openWhatsAppAndRegister();
                    }}
                    className="
                      inline-flex
                      items-center
                      justify-center
                      gap-2
                      rounded-xl
                      bg-amber-600
                      px-4
                      py-2
                      text-sm
                      font-semibold
                      text-white
                      hover:bg-amber-700
                    "
                  >
                    <MessageCircle size={17} />

                    Sim, abrir novamente
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-200 bg-white px-5 py-4">
          {!showAlreadyCompletedWarning && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handleOpenWhatsApp}
                disabled={
                  !message.trim() ||
                  updatingMonitoring
                }
                className="
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-green-600
                  px-4
                  py-2
                  font-semibold
                  text-white
                  hover:bg-green-700
                  disabled:cursor-not-allowed
                  disabled:opacity-50
                "
              >
                <MessageCircle size={18} />

               {updatingMonitoring
                  ? 'Registrando...'
                  : 'Abrir WhatsApp'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SetDeliveryModal({
  item,
  onClose,
  onDelivered,
  onOpenAgenda,
  toast,
}) {
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    delivery_date: today,
    notes: '',
    register_in_clinical_evolution: true,
    open_agenda_after_delivery: false,
  });

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const nextSetStart =
    item?.next_set_start_stage;

  const nextSetEnd =
    item?.next_set_end_stage;

  const setField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setFormError('');

    if (!form.delivery_date) {
      setFormError(
        'Informe a data da entrega ao paciente.'
      );
      return;
    }

    setSaving(true);

    try {
      await axios.post(
        `${API_URL}/aligner-treatments/${item.id}/deliver-next-set`,
        {
          delivery_date: form.delivery_date,
          notes: form.notes?.trim() || null,
          register_in_clinical_evolution:
            form.register_in_clinical_evolution === true,
        },
        authConfig()
      );

      toast?.success?.(
        `Conjunto de alinhadores ${nextSetStart} a ${nextSetEnd} entregue com sucesso.`
      );

      const shouldOpenAgenda =
        form.open_agenda_after_delivery === true;

      await onDelivered();

      onClose();

      if (shouldOpenAgenda) {
        onOpenAgenda?.(item);
      }
    } catch (error) {
      console.error(
        'Erro ao registrar entrega do conjunto:',
        error
      );

      const message = getApiErrorMessage(
        error,
        'Não foi possível registrar a entrega do conjunto.'
      );

      setFormError(message);
      toast?.error?.(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="
        fixed
        inset-0
        z-50
        flex
        items-start
        justify-center
        overflow-y-auto
        bg-black/50
        p-4
      "
      onMouseDown={() => {
        if (!saving) {
          onClose();
        }
      }}
    >
      <div
        className="
          mt-8
          w-full
          max-w-xl
          rounded-2xl
          bg-white
          shadow-2xl
        "
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Entregar conjunto ao paciente
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Registre a entrega e mantenha o histórico
              operacional e clínico do tratamento.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="
              rounded-lg
              p-2
              text-gray-500
              hover:bg-gray-100
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-5"
        >
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
              Conjunto a entregar
            </p>

            <p className="mt-1 text-lg font-bold text-blue-900">
              Alinhadores {nextSetStart} a {nextSetEnd}
            </p>

            {item?.next_set_received_at && (
              <p className="mt-1 text-sm text-blue-700">
                Recebido pela clínica em{' '}
                {formatDateBR(
                  item.next_set_received_at
                )}
              </p>
            )}
          </div>

          <div className="mt-5">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">
                Data da entrega ao paciente
              </span>

              <input
                type="date"
                value={form.delivery_date}
                max={today}
                onChange={(event) =>
                  setField(
                    'delivery_date',
                    event.target.value
                  )
                }
                className="
                  w-full
                  rounded-xl
                  border
                  border-gray-300
                  px-3
                  py-2
                  outline-none
                  focus:border-[var(--clinic-primary)]
                "
              />
            </label>
          </div>

          <div className="mt-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">
                Observações
              </span>

              <textarea
                rows={4}
                value={form.notes}
                onChange={(event) =>
                  setField(
                    'notes',
                    event.target.value
                  )
                }
                placeholder="Ex.: paciente orientado sobre uso, troca, higienização e próximo retorno."
                className="
                  w-full
                  resize-y
                  rounded-xl
                  border
                  border-gray-300
                  px-3
                  py-2
                  outline-none
                  focus:border-[var(--clinic-primary)]
                "
              />
            </label>
          </div>

          <label className="mt-4 flex items-start gap-3 rounded-xl border border-purple-200 bg-purple-50 p-4">
            <input
              type="checkbox"
              checked={
                form.register_in_clinical_evolution
              }
              onChange={(event) =>
                setField(
                  'register_in_clinical_evolution',
                  event.target.checked
                )
              }
              className="mt-0.5 h-4 w-4"
            />

            <span>
              <span className="block text-sm font-semibold text-purple-900">
                Registrar também na evolução clínica
              </span>

              <span className="mt-1 block text-xs leading-relaxed text-purple-700">
                Será criada uma evolução vinculada ao plano
                clínico deste tratamento com alinhadores.
              </span>
            </span>
          </label>

          <label className="mt-3 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <input
              type="checkbox"
              checked={
                form.open_agenda_after_delivery
              }
              onChange={(event) =>
                setField(
                  'open_agenda_after_delivery',
                  event.target.checked
                )
              }
              className="mt-0.5 h-4 w-4"
            />

            <span>
              <span className="block text-sm font-semibold text-blue-900">
                Agendar o próximo retorno após a entrega
              </span>

              <span className="mt-1 block text-xs leading-relaxed text-blue-700">
                Depois de registrar a entrega, a agenda será
                aberta com o paciente, o plano clínico, o
                tratamento de alinhadores e o profissional
                previamente selecionados.
              </span>
            </span>
          </label>

          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-800">
              Ao confirmar, o sistema irá:
            </p>

            <div className="mt-2 space-y-1 text-sm text-gray-600">
              <p>
                • tornar este conjunto o conjunto atual;
              </p>

              <p>
                • atualizar o estágio operacional até o
                alinhador {nextSetEnd};
              </p>

              <p>
                • limpar os campos do próximo conjunto;
              </p>

              <p>
                • registrar a entrega no histórico.
              </p>
              {form.open_agenda_after_delivery && (
                <p>
                  • abrir a agenda para marcar o próximo
                  retorno.
                </p>
              )}
            </div>
          </div>

          {formError && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="
                rounded-xl
                border
                border-gray-300
                bg-white
                px-4
                py-2
                text-sm
                font-semibold
                text-gray-700
                hover:bg-gray-50
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={saving}
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                rounded-xl
                bg-blue-600
                px-4
                py-2
                text-sm
                font-semibold
                text-white
                hover:bg-blue-700
                disabled:cursor-not-allowed
                disabled:opacity-60
              "
            >
              <CheckCircle2 size={17} />

              {saving
                ? 'Registrando entrega...'
                : form.open_agenda_after_delivery
                ? 'Entregar e abrir agenda'
                : 'Confirmar entrega'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FollowUpModal({
  item,
  onClose,
  onSaved,
  toast,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const initialSituation = getSimpleSituation(item);

  const [form, setForm] = useState({
    event_date: today,
    event_type: 'in_person_visit',
    description: '',
    phase_status_after: initialSituation,
    next_follow_up_date: item?.next_follow_up_date || '',
    register_in_clinical_evolution: true,
    next_set_received: item?.next_set_received === true,
    next_set_received_at: item?.next_set_received_at || '',
    next_set_start_stage: item?.next_set_start_stage ?? '',
    next_set_end_stage: item?.next_set_end_stage ?? '',
    total_stages: item?.total_stages || '',
    all_aligners_delivered: hasAllAlignersDelivered(item),
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const setField = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const typeLabels = {
    in_person_visit: 'Consulta presencial',
    virtual_visit: 'Consulta virtual',
    patient_contact: 'Contato por WhatsApp ou telefone',
    other_follow_up: 'Outro acompanhamento',
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      await axios.post(
        `${API_URL}/aligner-treatments/${item.id}/events`,
        {
          event_type: form.event_type,
          event_date: form.event_date,
          title: typeLabels[form.event_type],
          description: form.description?.trim() || null,
          professional_id: item?.professional_id || null,
          professional_name: item?.professional_name || null,
          register_in_clinical_evolution:
            form.register_in_clinical_evolution === true,
          phase_status_after: form.phase_status_after,
          all_aligners_delivered: form.all_aligners_delivered === true,
          next_follow_up_date:
            ['completed', 'paused', 'cancelled'].includes(
              form.phase_status_after
            )
              ? null
              : form.next_follow_up_date || null,
          next_set_received:
            !['completed', 'awaiting_retention'].includes(form.phase_status_after) &&
            form.all_aligners_delivered !== true &&
            form.next_set_received === true,
          next_set_received_at:
            !['completed', 'awaiting_retention'].includes(form.phase_status_after) &&
            form.all_aligners_delivered !== true &&
            form.next_set_received === true
              ? form.next_set_received_at || form.event_date || today
              : null,
          next_set_start_stage: form.next_set_received && !form.all_aligners_delivered
            ? numericOrNull(form.next_set_start_stage)
            : null,
          next_set_end_stage: form.next_set_received && !form.all_aligners_delivered
            ? numericOrNull(form.next_set_end_stage)
            : null,
          total_stages: numericOrNull(form.total_stages),
        },
        authConfig()
      );

      toast?.success?.('Acompanhamento registrado com sucesso.');
      await onSaved();
      onClose();
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        'Não foi possível registrar o acompanhamento.'
      );
      setFormError(message);
      toast?.error?.(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-200 p-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Registrar acompanhamento
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {item.patient_name}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-5 p-5">
          {formError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {formError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Data</span>
              <input type="date" value={form.event_date} max={today} onChange={(event) => setField('event_date', event.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-2" required />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Tipo</span>
              <select value={form.event_type} onChange={(event) => setField('event_type', event.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-2">
                {Object.entries(typeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Observação <span className="font-normal text-gray-400">(opcional)</span></span>
            <textarea rows={4} value={form.description} onChange={(event) => setField('description', event.target.value)} placeholder="Registre somente o que for importante sobre o acompanhamento." className="w-full resize-y rounded-xl border border-gray-300 px-3 py-2" />
          </label>

          <section className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4">
            <h3 className="font-semibold text-gray-900">E agora?</h3>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Situação após este acompanhamento</span>
                <select value={form.phase_status_after} onChange={(event) => setField('phase_status_after', event.target.value)} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2">
                  <option value="awaiting_documents">Aguardando documentação/exames</option>
                  <option value="awaiting_aligners">Aguardando alinhadores</option>
                  <option value="follow_up_only">Em acompanhamento</option>
                  <option value="awaiting_refinement">Aguardando refinamento / novos alinhadores</option>
                  <option value="refinement_active">Em refinamento</option>
                  <option value="awaiting_retention">Aguardando contenção</option>
                  <option value="completed">Finalizado</option>
                </select>
              </label>

              {!['completed', 'paused', 'cancelled'].includes(form.phase_status_after) && (
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Lembrar novamente em</span>
                  <input type="date" value={form.next_follow_up_date} onChange={(event) => setField('next_follow_up_date', event.target.value)} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2" />
                </label>
              )}
            </div>
          </section>

          {!['completed', 'awaiting_retention'].includes(form.phase_status_after) && form.all_aligners_delivered !== true && <section className="rounded-2xl border border-purple-200 bg-purple-50 p-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={form.next_set_received}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setForm((current) => ({
                    ...current,
                    next_set_received: checked,
                    next_set_received_at: checked
                      ? current.next_set_received_at || today
                      : '',
                  }));
                }}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-semibold text-purple-900">Os próximos alinhadores chegaram à clínica</span>
                <span className="mt-1 block text-xs text-purple-700">Use como controle interno do material que já está na clínica.</span>
              </span>
            </label>
            {form.next_set_received && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-purple-900">Alinhadores recebidos</span>
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" required value={form.next_set_start_stage} onChange={(event) => setField('next_set_start_stage', event.target.value)} placeholder="Do" className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2" />
                    <span className="text-sm text-purple-800">até</span>
                    <input type="number" min="1" required value={form.next_set_end_stage} onChange={(event) => setField('next_set_end_stage', event.target.value)} placeholder="Até" className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2" />
                  </div>
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-purple-900">Total previsto no tratamento</span>
                  <input type="number" min="1" value={form.total_stages} onChange={(event) => setField('total_stages', event.target.value)} placeholder="Ex.: 20" className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-sm font-medium text-purple-900">Data da chegada</span>
                  <input type="date" value={form.next_set_received_at} onChange={(event) => setField('next_set_received_at', event.target.value)} className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2 sm:w-64" />
                </label>
              </div>
            )}
          </section>}

          {form.phase_status_after !== 'completed' && <label className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
            <input
              type="checkbox"
              checked={form.all_aligners_delivered === true}
              onChange={(event) => {
                const checked = event.target.checked;
                setForm((current) => ({
                  ...current,
                  all_aligners_delivered: checked,
                  next_set_received: checked ? false : current.next_set_received,
                  next_set_received_at: checked ? '' : current.next_set_received_at,
                  next_set_start_stage: checked ? '' : current.next_set_start_stage,
                  next_set_end_stage: checked ? '' : current.next_set_end_stage,
                }));
              }}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              <span className="block text-sm font-semibold text-green-900">Todos os alinhadores já foram entregues ao paciente</span>
              <span className="mt-1 block text-xs text-green-700">Pode ser marcado junto com qualquer situação clínica, inclusive “Em refinamento”.</span>
            </span>
          </label>}

          <label className="flex items-start gap-3 rounded-xl border border-purple-200 bg-purple-50 p-4">
            <input type="checkbox" checked={form.register_in_clinical_evolution} onChange={(event) => setField('register_in_clinical_evolution', event.target.checked)} className="mt-0.5 h-4 w-4" />
            <span>
              <span className="block text-sm font-semibold text-purple-900">Registrar também na evolução clínica</span>
              <span className="mt-1 block text-xs text-purple-700">Evita repetir esta anotação no prontuário.</span>
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-gray-300 px-4 py-2 font-medium text-gray-700">Cancelar</button>
          <button type="submit" disabled={saving} className="rounded-xl bg-[var(--clinic-primary)] px-4 py-2 font-semibold text-white disabled:opacity-60">
            {saving ? 'Registrando...' : 'Registrar acompanhamento'}
          </button>
        </div>
      </form>
    </div>
  );
}

function MonitoringModal({
  item,
  onClose,
  onSaved,
  toast,
}) {
  const [form, setForm] = useState(() =>
    buildMonitoringForm(item)
  );

  const [saving, setSaving] = useState(false);

  const [formError, setFormError] = useState('');

  const setField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const calculatedIntervalDays =
    form.follow_up_interval_mode === 'fixed_days'
      ? Math.max(
          1,
          Number(form.follow_up_interval_days || 1)
        )
      : Math.max(
          1,
          Number(form.wear_days_per_aligner || 1)
        ) *
        Math.max(
          1,
          Number(form.follow_up_aligners_count || 1)
        );

  const handleSave = async (event) => {
    event.preventDefault();

    setFormError('');

    setSaving(true);

    try {
      const payload = {
        phase_status: form.phase_status,

        next_follow_up_date:
          form.next_follow_up_date || null,

        follow_up_interval_mode:
          form.follow_up_interval_mode,

        wear_days_per_aligner: Math.max(
          1,
          Number(form.wear_days_per_aligner || 1)
        ),

        follow_up_aligners_count: Math.max(
          1,
          Number(form.follow_up_aligners_count || 1)
        ),

        follow_up_interval_days: Math.max(
          1,
          Number(form.follow_up_interval_days || 1)
        ),

        attention_days_before: Math.max(
          0,
          Number(form.attention_days_before || 0)
        ),

        urgent_after_days: Math.max(
          1,
          Number(form.urgent_after_days || 1)
        ),

        manual_last_clinical_visit_date:
          form.manual_last_clinical_visit_date || null,

        current_set_start_stage:
          numericOrNull(
            form.current_set_start_stage
          ),

        current_set_end_stage:
          numericOrNull(
            form.current_set_end_stage
          ),

        current_set_delivered_at:
          form.current_set_delivered_at || null,

        next_set_start_stage:
          numericOrNull(
            form.next_set_start_stage
          ),

        next_set_end_stage:
          numericOrNull(
            form.next_set_end_stage
          ),

        total_stages:
          numericOrNull(form.total_stages),

        next_set_received:
          !['completed', 'awaiting_retention'].includes(form.phase_status) &&
          form.all_aligners_delivered !== true &&
          form.next_set_received === true,

        next_set_received_at:
          !['completed', 'awaiting_retention'].includes(form.phase_status) &&
          form.all_aligners_delivered !== true &&
          form.next_set_received &&
          form.next_set_received_at
            ? form.next_set_received_at
            : null,

        all_aligners_delivered:
          form.all_aligners_delivered === true,

        initial_guidance_completed:
          form.initial_guidance_completed === true,

        adaptation_message_sent:
          form.adaptation_message_sent === true,

        first_aligner_change_followed:
          form.first_aligner_change_followed === true,
      };

      await axios.patch(
        `${API_URL}/aligner-treatments/${item.id}/monitoring`,
        payload,
        authConfig()
      );

      toast?.success?.(
        'Acompanhamento atualizado com sucesso.'
      );

      await onSaved();
      onClose();
    } catch (error) {
      console.error(
        'Erro ao atualizar acompanhamento:',
        error
      );

      const message = getApiErrorMessage(
        error,
        'Não foi possível atualizar o acompanhamento.'
      );

      setFormError(message);

      toast?.error?.(message);
    } finally {
      setSaving(false);
    }
  };

  const renderNumberField = (
    label,
    field,
    minimum = 0
  ) => (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">
        {label}
      </span>

      <input
        type="number"
        min={minimum}
        value={form[field]}
        onChange={(event) =>
          setField(field, event.target.value)
        }
        className="
          w-full
          rounded-xl
          border
          border-gray-300
          px-3
          py-2
          outline-none
          focus:border-[var(--clinic-primary)]
        "
      />
    </label>
  );

  const renderDateField = (label, field) => (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">
        {label}
      </span>

      <input
        type="date"
        value={form[field]}
        onChange={(event) =>
          setField(field, event.target.value)
        }
        className="
          w-full
          rounded-xl
          border
          border-gray-300
          px-3
          py-2
          outline-none
          focus:border-[var(--clinic-primary)]
        "
      />
    </label>
  );

  const renderCheckbox = (label, field) => (
    <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-3">
      <input
        type="checkbox"
        checked={form[field]}
        onChange={(event) =>
          setField(field, event.target.checked)
        }
        className="mt-0.5 h-4 w-4"
      />

      <span className="text-sm font-medium text-gray-700">
        {label}
      </span>
    </label>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !saving
        ) {
          onClose();
        }
      }}
    >
      <form
        onMouseDown={(event) =>
          event.stopPropagation()
        }
        onSubmit={handleSave}
        className="
          max-h-[92vh]
          w-full
          max-w-3xl
          overflow-y-auto
          rounded-2xl
          bg-white
          shadow-2xl
        "
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Configurar acompanhamento
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              {item.patient_name}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6 p-5">
          {formError && (
            <div
              role="alert"
              className="
                flex
                items-start
                gap-3
                rounded-xl
                border
                border-red-200
                bg-red-50
                px-4
                py-3
                text-sm
                text-red-800
              "
            >
              <AlertCircle
                size={19}
                className="mt-0.5 shrink-0"
              />

              <div className="flex-1">
                <p className="font-semibold">
                  Não foi possível salvar
                </p>

                <p className="mt-1">
                  {formError}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setFormError('')}
                className="rounded p-1 hover:bg-red-100"
                aria-label="Fechar mensagem de erro"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <section className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4">
            <h3 className="font-semibold text-gray-900">
              Situação atual
            </h3>

            <p className="mt-1 text-sm text-gray-600">
              Informe apenas onde o paciente está hoje. Não é necessário reconstruir etapas anteriores.
            </p>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Como está o tratamento?
                </span>

                <select
                  value={form.phase_status}
                  onChange={(event) =>
                    setField('phase_status', event.target.value)
                  }
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2"
                >
                  <option value="awaiting_documents">Aguardando documentação/exames</option>
                  <option value="awaiting_aligners">Aguardando alinhadores</option>
                  <option value="follow_up_only">Em acompanhamento</option>
                  <option value="awaiting_refinement">Aguardando refinamento / novos alinhadores</option>
                  <option value="refinement_active">Em refinamento</option>
                  <option value="awaiting_retention">Aguardando contenção</option>
                  <option value="completed">Finalizado</option>
                </select>
              </label>

              {!['completed', 'paused', 'cancelled'].includes(
                form.phase_status
              ) && renderDateField(
                'Lembrar novamente em',
                'next_follow_up_date'
              )}
            </div>

          </section>

          {!['completed', 'awaiting_retention'].includes(form.phase_status) && form.all_aligners_delivered !== true && <section className="rounded-2xl border border-purple-200 bg-purple-50 p-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={form.next_set_received}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setForm((current) => ({
                    ...current,
                    next_set_received: checked,
                    next_set_received_at: checked
                      ? current.next_set_received_at || new Date().toISOString().slice(0, 10)
                      : '',
                  }));
                }}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-semibold text-purple-900">
                  Os próximos alinhadores chegaram à clínica
                </span>
                <span className="mt-1 block text-xs text-purple-700">
                  Marque somente para registrar que os próximos alinhadores já estão na clínica.
                </span>
              </span>
            </label>

            {form.next_set_received && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-purple-900">Alinhadores recebidos</span>
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" required value={form.next_set_start_stage} onChange={(event) => setField('next_set_start_stage', event.target.value)} placeholder="Do" className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2" />
                    <span className="text-sm text-purple-800">até</span>
                    <input type="number" min="1" required value={form.next_set_end_stage} onChange={(event) => setField('next_set_end_stage', event.target.value)} placeholder="Até" className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2" />
                  </div>
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-purple-900">Total previsto no tratamento</span>
                  <input type="number" min="1" value={form.total_stages} onChange={(event) => setField('total_stages', event.target.value)} placeholder="Ex.: 20" className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-sm font-medium text-purple-900">Data da chegada</span>
                  <input type="date" value={form.next_set_received_at} onChange={(event) => setField('next_set_received_at', event.target.value)} className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2 md:w-64" />
                </label>
              </div>
            )}
          </section>}

          {form.phase_status !== 'completed' && <label className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
            <input
              type="checkbox"
              checked={form.all_aligners_delivered === true}
              onChange={(event) => {
                const checked = event.target.checked;
                setForm((current) => ({
                  ...current,
                  all_aligners_delivered: checked,
                  next_set_received: checked ? false : current.next_set_received,
                  next_set_received_at: checked ? '' : current.next_set_received_at,
                  next_set_start_stage: checked ? '' : current.next_set_start_stage,
                  next_set_end_stage: checked ? '' : current.next_set_end_stage,
                }));
              }}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              <span className="block text-sm font-semibold text-green-900">Todos os alinhadores já foram entregues ao paciente</span>
              <span className="mt-1 block text-xs text-green-700">Esta informação não altera a situação clínica atual.</span>
            </span>
          </label>}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-200 bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="
              rounded-xl
              border
              border-gray-300
              px-4
              py-2
              font-medium
              text-gray-700
              hover:bg-gray-50
              disabled:opacity-60
            "
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={saving}
            className="
              rounded-xl
              bg-[var(--clinic-primary)]
              px-4
              py-2
              font-semibold
              text-white
              hover:opacity-90
              disabled:opacity-60
            "
          >
            {saving
              ? 'Salvando...'
              : 'Salvar acompanhamento'}
          </button>
        </div>
      </form>
    </div>
  );
}

const AlignersPage = ({
  embedded = false,
  patientId = null,
  renderExtraActions = null,
  onDataUpdated = null,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const toast = useMemo(
    () => ({
      success: (message) =>
        showToast(message, 'success'),

      error: (message) =>
        showToast(message, 'error'),

      warning: (message) =>
        showToast(message, 'warning'),

      info: (message) =>
        showToast(message, 'info'),
    }),
    [showToast]
  );

  const [loading, setLoading] = useState(true);

  const [
    alignersPermissionChecked,
    setAlignersPermissionChecked,
  ] = useState(false);

  const [
    hasAlignersModule,
    setHasAlignersModule,
  ] = useState(false);

  const [dashboard, setDashboard] = useState({
    summary: {},
    items: [],
  });

  const [search, setSearch] = useState('');
  const [professionalFilter, setProfessionalFilter] =
    useState('all');
  const [providerFilter, setProviderFilter] =
    useState('all');
  const [treatmentStatusFilter, setTreatmentStatusFilter] =
    useState('all');
  const [monitoringFilter, setMonitoringFilter] =
    useState('all');

  const [operationalFilter, setOperationalFilter] =
    useState('all');

  const [
    recommendedActionFilter,
    setRecommendedActionFilter,
  ] = useState('all');

  const [healthFilter, setHealthFilter] =
    useState('all');

  const [sortBy, setSortBy] =
    useState('priority');

  const [
    selectedWhatsAppTreatment,
    setSelectedWhatsAppTreatment,
  ] = useState(null);

  const [
    selectedMonitoringTreatment,
    setSelectedMonitoringTreatment,
  ] = useState(null);

  const [
    selectedFollowUpTreatment,
    setSelectedFollowUpTreatment,
  ] = useState(null);

  const [
    selectedSetDeliveryTreatment,
    setSelectedSetDeliveryTreatment,
  ] = useState(null);

  const loadAlignersPermission = useCallback(async () => {
    setHasAlignersModule(true);
    setAlignersPermissionChecked(true);
  }, []);
  
  const loadDashboard = useCallback(async () => {
    if (
      !alignersPermissionChecked ||
      !hasAlignersModule
    ) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}/aligner-treatments/dashboard`,
        {
          ...authConfig(),
          params: patientId
            ? {
                patient_id: patientId,
              }
            : undefined,
        }
      );

      setDashboard({
        summary: response.data?.summary || {},
        items: Array.isArray(response.data?.items)
          ? response.data.items
          : [],
      });
    } catch (error) {
      console.error(
        'Erro ao carregar Central de Alinhadores:',
        error
      );

      toast?.error?.(
        'Não foi possível carregar a Central de Alinhadores.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    toast,
    patientId,
    alignersPermissionChecked,
    hasAlignersModule,
  ]);

  const refreshAfterMutation = useCallback(async () => {
    await loadDashboard();

    if (typeof onDataUpdated === 'function') {
      await onDataUpdated();
    }
  }, [loadDashboard, onDataUpdated]);

  useEffect(() => {
    loadAlignersPermission();
  }, [loadAlignersPermission]);

  useEffect(() => {
    if (
      alignersPermissionChecked &&
      hasAlignersModule
    ) {
      loadDashboard();
    }
  }, [
    alignersPermissionChecked,
    hasAlignersModule,
    loadDashboard,
  ]);

  const items = dashboard.items || [];
  const summary = dashboard.summary || {};

  const professionals = useMemo(() => {
    const map = new Map();

    items.forEach((item) => {
      if (!item.professional_id) {
        return;
      }

      map.set(item.professional_id, {
        id: item.professional_id,
        name:
          item.professional_name ||
          'Profissional não informado',
      });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, 'pt-BR')
    );
  }, [items]);

  const providers = useMemo(() => {
    return Array.from(
      new Set(
        items
          .map((item) => item.provider)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [items]);

  const operationalSummary = useMemo(() => {
    return items.reduce(
      (summary, item) => {
        if (!item.has_scheduled_return) {
          summary.noScheduledReturn += 1;
        }

        if (
          item.next_set_received === true
        ) {
          summary.nextSetWaitingDelivery += 1;
        }

        if (hasInitialActionsPending(item)) {
          summary.initialActionsPending += 1;
        }

        return summary;
      },
      {
        noScheduledReturn: 0,
        nextSetWaitingDelivery: 0,
        initialActionsPending: 0,
      }
    );
  }, [items]);

  const recommendedActionSummary = useMemo(() => {
    const initialSummary = Object.keys(
      RECOMMENDED_ACTION_CARDS
    ).reduce((summary, actionId) => {
      summary[actionId] = 0;
      return summary;
    }, {});

    return items.reduce((summary, item) => {
      const templates =
        buildWhatsAppTemplates(item);

      const recommendedActionId =
        getRecommendedWhatsAppTemplateId(
          item,
          templates
        );

      if (
        Object.prototype.hasOwnProperty.call(
          summary,
          recommendedActionId
        )
      ) {
        summary[recommendedActionId] += 1;
      }

      return summary;
    }, initialSummary);
  }, [items]);

  const productionSummary = useMemo(() => {
    return items.reduce(
      (summary, item) => {
        const nextSetWaitingDelivery =
          item.next_set_received === true;

        if (nextSetWaitingDelivery) {
          summary.waitingDelivery += 1;
        }

        const appointmentDate =
          item?.next_appointment?.appointment_date;

        if (
          item.has_scheduled_return === true &&
          isSameLocalDate(appointmentDate)
        ) {
          summary.returnsToday += 1;
        }

        if (
          ['urgent', 'overdue'].includes(
            item.monitoring_status
          )
        ) {
          summary.delayed += 1;
        }

        const templates =
          buildWhatsAppTemplates(item);

        const recommendedAction =
          getRecommendedWhatsAppTemplateId(
            item,
            templates
          );

        if (recommendedAction) {
          summary.pendingMessages += 1;
        }

        const treatmentStatus =
          item.status ||
          item.treatment_status;

        if (
          [
            'planned',
            'in_progress',
            'refinement',
            'paused',
          ].includes(treatmentStatus)
        ) {
          summary.activeTreatments += 1;
        }

        return summary;
      },
      {
        waitingDelivery: 0,
        returnsToday: 0,
        delayed: 0,
        pendingMessages: 0,
        activeTreatments: 0,
      }
    );
  }, [items]);

  const healthSummary = useMemo(() => {
    const summary = {
      critical: 0,
      attention: 0,
      healthy: 0,
      notScored: 0,
      evaluated: 0,
      totalScore: 0,
      averageScore: null,
    };

    items.forEach((item) => {
      const diagnosis =
        getTreatmentHealthDiagnosis(item);

      if (diagnosis.score === null) {
        summary.notScored += 1;
        return;
      }

      summary.evaluated += 1;
      summary.totalScore += diagnosis.score;

      if (diagnosis.level === 'critical') {
        summary.critical += 1;
      } else if (
        diagnosis.level === 'attention'
      ) {
        summary.attention += 1;
      } else if (
        diagnosis.level === 'healthy'
      ) {
        summary.healthy += 1;
      }
    });

    summary.averageScore =
      summary.evaluated > 0
        ? Math.round(
            summary.totalScore /
              summary.evaluated
          )
        : null;

    return summary;
  }, [items]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = search
      .trim()
      .toLocaleLowerCase('pt-BR');

    return items.filter((item) => {
      const treatmentStatus =
        item.status ||
        item.treatment_status;

      if (
        professionalFilter !== 'all' &&
        item.professional_id !== professionalFilter
      ) {
        return false;
      }

      if (
        providerFilter !== 'all' &&
        item.provider !== providerFilter
      ) {
        return false;
      }

      if (
        treatmentStatusFilter !== 'all' &&
        treatmentStatus !== treatmentStatusFilter
      ) {
        return false;
      }

      if (
        monitoringFilter === 'not_on_time' &&
        !['attention', 'overdue', 'urgent', 'needs_review'].includes(
          item.monitoring_status
        )
      ) {
        return false;
      }

      if (
        !['all', 'not_on_time'].includes(monitoringFilter) &&
        item.monitoring_status !== monitoringFilter
      ) {
        return false;
      }

      if (
        operationalFilter === 'scheduled_return' &&
        !item.has_scheduled_return
      ) {
        return false;
      }

      if (
        operationalFilter ===
          'no_scheduled_return' &&
        item.has_scheduled_return
      ) {
        return false;
      }

      if (
        operationalFilter === 'next_set_received' &&
        (
          item.next_set_received !== true
        )
      ) {
        return false;
      }

      if (
        operationalFilter === 'next_set_pending' &&
        (
          item.next_set_start_stage === null ||
          item.next_set_start_stage === undefined ||
          item.next_set_received === true
        )
      ) {
        return false;
      }

      if (
        operationalFilter ===
          'initial_actions_pending' &&
        !hasInitialActionsPending(item)
      ) {
        return false;
      }

      if (
        operationalFilter === 'return_today'
      ) {
        const appointmentDate =
          item?.next_appointment?.appointment_date;

        if (
          item.has_scheduled_return !== true ||
          !isSameLocalDate(appointmentDate)
        ) {
          return false;
        }
      }

      if (
        operationalFilter ===
          'overdue_or_urgent' &&
        !['urgent', 'overdue'].includes(
          item.monitoring_status
        )
      ) {
        return false;
      }

      if (
        operationalFilter ===
          'active_treatments' &&
        ![
          'planned',
          'in_progress',
          'refinement',
          'paused',
        ].includes(treatmentStatus)
      ) {
        return false;
      }

      if (recommendedActionFilter !== 'all') {
        const itemTemplates =
          buildWhatsAppTemplates(item);

        const itemRecommendedAction =
          getRecommendedWhatsAppTemplateId(
            item,
            itemTemplates
          );

        if (
          recommendedActionFilter !==
            'any_pending' &&
          itemRecommendedAction !==
            recommendedActionFilter
        ) {
          return false;
        }

        if (
          recommendedActionFilter ===
            'any_pending' &&
          !itemRecommendedAction
        ) {
          return false;
        }
      }

      if (healthFilter !== 'all') {
        const healthDiagnosis =
          getTreatmentHealthDiagnosis(item);

        if (
          healthFilter === 'not_scored' &&
          healthDiagnosis.score !== null
        ) {
          return false;
        }

        if (
          healthFilter !== 'not_scored' &&
          healthDiagnosis.level !== healthFilter
        ) {
          return false;
        }
      }

      if (normalizedSearch) {
        const searchable = [
          item.patient_name,
          item.patient_phone,
          item.patient_email,
          item.provider,
          item.professional_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase('pt-BR');

        if (!searchable.includes(normalizedSearch)) {
          return false;
        }
      }

      return true;
    });
  }, [
    items,
    search,
    professionalFilter,
    providerFilter,
    treatmentStatusFilter,
    monitoringFilter,
    operationalFilter,
    recommendedActionFilter,
    healthFilter,
  ]);

  const sortedItems = useMemo(() => {
    const result = [...filteredItems];

    result.sort((a, b) => {
      if (sortBy === 'patient') {
        return String(a.patient_name || '').localeCompare(
          String(b.patient_name || ''),
          'pt-BR'
        );
      }

      if (
        sortBy === 'health_ascending' ||
        sortBy === 'health_descending'
      ) {
        const aDiagnosis =
          getTreatmentHealthDiagnosis(a);

        const bDiagnosis =
          getTreatmentHealthDiagnosis(b);

        const aScore = aDiagnosis.score;
        const bScore = bDiagnosis.score;

        /*
         * Tratamentos sem score ficam depois dos
         * tratamentos efetivamente avaliados.
         */

        if (
          aScore === null &&
          bScore === null
        ) {
          return String(
            a.patient_name || ''
          ).localeCompare(
            String(b.patient_name || ''),
            'pt-BR'
          );
        }

        if (aScore === null) {
          return 1;
        }

        if (bScore === null) {
          return -1;
        }

        const scoreDifference =
          sortBy === 'health_ascending'
            ? aScore - bScore
            : bScore - aScore;

        if (scoreDifference !== 0) {
          return scoreDifference;
        }

        /*
         * Em caso de empate, prevalece a situação
         * clínica mais urgente.
         */

        const priorityDifference =
          getOperationalPriority(a) -
          getOperationalPriority(b);

        if (priorityDifference !== 0) {
          return priorityDifference;
        }

        return String(
          a.patient_name || ''
        ).localeCompare(
          String(b.patient_name || ''),
          'pt-BR'
        );
      }

      if (sortBy === 'next_action') {
        const aDate = parseLocalDate(
          a.next_recommended_review_date
        );

        const bDate = parseLocalDate(
          b.next_recommended_review_date
        );

        if (!aDate && !bDate) {
          return String(a.patient_name || '').localeCompare(
            String(b.patient_name || ''),
            'pt-BR'
          );
        }

        if (!aDate) {
          return 1;
        }

        if (!bDate) {
          return -1;
        }

        return aDate.getTime() - bDate.getTime();
      }

      const clinicalPriorityDifference =
        getOperationalPriority(a) -
        getOperationalPriority(b);

      if (clinicalPriorityDifference !== 0) {
        return clinicalPriorityDifference;
      }

      const actionWeight = {
        schedule_return: 1,
        initial_guidance: 2,
        follow_up: 3,
        first_change: 4,
        scheduled_return: 5,
      };

      const aAction =
        getRecommendedWhatsAppTemplateId(
          a,
          buildWhatsAppTemplates(a)
        );

      const bAction =
        getRecommendedWhatsAppTemplateId(
          b,
          buildWhatsAppTemplates(b)
        );

      const actionDifference =
        (actionWeight[aAction] ?? 99) -
        (actionWeight[bAction] ?? 99);

      if (actionDifference !== 0) {
        return actionDifference;
      }

      const aDays =
        differenceInCalendarDays(
          a.next_recommended_review_date
        ) ?? Number.MAX_SAFE_INTEGER;

      const bDays =
        differenceInCalendarDays(
          b.next_recommended_review_date
        ) ?? Number.MAX_SAFE_INTEGER;

      if (aDays !== bDays) {
        return aDays - bDays;
      }

      return String(a.patient_name || '').localeCompare(
        String(b.patient_name || ''),
        'pt-BR'
      );
    });

    return result;
  }, [filteredItems, sortBy]);

  const resetIndicatorFilters = () => {
    setSearch('');
    setProfessionalFilter('all');
    setProviderFilter('all');
    setTreatmentStatusFilter('all');
    setMonitoringFilter('all');
    setOperationalFilter('all');
    setRecommendedActionFilter('all');
    setHealthFilter('all');
  };

  const showAllTreatments = () => {
    resetIndicatorFilters();
  };

  const toggleMonitoringFilter = (value) => {
    const nextValue = monitoringFilter === value ? 'all' : value;
    resetIndicatorFilters();
    setMonitoringFilter(nextValue);
  };

  const toggleOperationalFilter = (value) => {
    const nextValue = operationalFilter === value ? 'all' : value;
    resetIndicatorFilters();
    setOperationalFilter(nextValue);
  };

  const toggleRecommendedActionFilter = (
    actionId
  ) => {
    const nextValue =
      recommendedActionFilter === actionId ? 'all' : actionId;
    resetIndicatorFilters();
    setRecommendedActionFilter(nextValue);
  };

  const toggleHealthFilter = (value) => {
    const nextValue = healthFilter === value ? 'all' : value;
    resetIndicatorFilters();
    setHealthFilter(nextValue);
  };

  const handleProductionCardClick = (
    cardId
  ) => {
    if (cardId === 'pending_messages') {
      toggleRecommendedActionFilter('any_pending');
      return;
    }

    const config =
      PRODUCTION_PANEL_CARDS[cardId];

    const filterValue =
      config?.operationalFilter;

    if (!filterValue) {
      return;
    }

    toggleOperationalFilter(filterValue);
  };

  const clearFilters = () => {
    setSearch('');
    setProfessionalFilter('all');
    setProviderFilter('all');
    setTreatmentStatusFilter('all');
    setMonitoringFilter('all');
    setOperationalFilter('all');
    setRecommendedActionFilter('all');
    setHealthFilter('all');
    setSortBy('priority');
  };

  const openWhatsApp = (item) => {
    if (!onlyDigits(item.patient_phone)) {
      toast?.error?.(
        'Paciente sem telefone cadastrado.'
      );
      return;
    }

    setSelectedWhatsAppTreatment(item);
  };

  const openAgenda = (item) => {
    const params = new URLSearchParams();

    params.set('new', '1');

    if (item.patient_id) {
      params.set(
        'patientId',
        item.patient_id
      );
    }

    if (item.linked_treatment_id) {
      params.set(
        'treatmentId',
        item.linked_treatment_id
      );
    }

    if (item.id) {
      params.set(
        'alignerTreatmentId',
        item.id
      );
    }

    if (item.professional_id) {
      params.set(
        'professionalId',
        item.professional_id
      );
    }

    navigate(
      `/agenda?${params.toString()}`
    );
  };

  const openPatientRecord = (item) => {
    navigate(
      `/prontuarios?patientId=${encodeURIComponent(
        item.patient_id
      )}`
    );
  };

  const PageWrapper = embedded
    ? React.Fragment
    : Layout;

  return (
    <PageWrapper>
      <div
        className={
          embedded
            ? 'space-y-4'
            : 'mx-auto max-w-7xl space-y-6 p-4 md:p-6'
        }
      >

        {!embedded && (
          <>

            <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Central de Alinhadores
                </h1>

                <p className="mt-1 text-sm text-gray-600">
                  Acompanhe tratamentos, retornos e pacientes que
                  precisam de atenção.
                </p>
              </div>

              <button
                type="button"
                onClick={loadDashboard}
                disabled={loading}
                className="
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  py-2
                  font-medium
                  text-gray-700
                  shadow-sm
                  hover:bg-gray-50
                  disabled:opacity-60
                "
              >
                <RefreshCw
                  size={18}
                  className={loading ? 'animate-spin' : ''}
                />

                Atualizar
              </button>
            </header>

            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
              <button
                type="button"
                onClick={showAllTreatments}
                className={`
                  rounded-2xl
                  border
                  border-blue-200
                  bg-blue-50
                  p-4
                  text-left
                  shadow-sm
                  transition
                  hover:-translate-y-0.5
                  hover:shadow-md
                  ${
                    monitoringFilter === 'all'
                      ? 'ring-2 ring-[var(--clinic-primary)] ring-offset-2'
                      : ''
                  }
                `}
              >
                <p className="text-sm font-medium text-blue-800">
                  Em tratamento
                </p>

                <p className="mt-2 text-3xl font-bold text-blue-950">
                  {summary.total_active || 0}
                </p>
              </button>

              {[
                'on_time',
                'attention',
                'overdue',
                'urgent',
                'needs_review',
              ].map((status) => (
                <SummaryCard
                  key={status}
                  title={MONITORING_CONFIG[status].label}
                  value={summary[status]}
                  status={status}
                  active={monitoringFilter === status}
                  onClick={() => toggleMonitoringFilter(status)}
                />
              ))}

              <button
                type="button"
                onClick={() =>
                  toggleOperationalFilter('scheduled_return')
                }
                className={`
                  rounded-2xl
                  border
                  p-4
                  text-left
                  shadow-sm
                  transition
                  hover:-translate-y-0.5
                  hover:shadow-md
                  ${
                    operationalFilter === 'scheduled_return'
                      ? 'border-cyan-400 bg-cyan-50 ring-2 ring-cyan-200'
                      : 'border-cyan-200 bg-cyan-50'
                  }
                `}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-cyan-800">
                      Retorno agendado
                    </p>

                    <p className="mt-2 text-3xl font-bold text-cyan-950">
                      {summary.scheduled_return || 0}
                    </p>
                  </div>

                  <span className="rounded-xl bg-cyan-100 p-2 text-cyan-800">
                    <CalendarDays size={21} />
                  </span>
                </div>
              </button>
            </section>

            <button
              type="button"
                onClick={() => toggleMonitoringFilter('not_on_time')}
              className={`w-full rounded-xl border px-4 py-2 text-left text-sm font-semibold transition ${
                monitoringFilter === 'not_on_time'
                  ? 'border-purple-400 bg-purple-50 ring-2 ring-purple-200'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Ver os pacientes que não estão em dia ({
                (summary.attention || 0) +
                (summary.overdue || 0) +
                (summary.urgent || 0) +
                (summary.needs_review || 0)
              })
            </button>

            <section className="grid gap-3 sm:grid-cols-3">
              <button
                type="button"
                onClick={() =>
                  toggleOperationalFilter(
                    'no_scheduled_return'
                  )
                }
                className={`
                  flex
                  items-center
                  justify-between
                  gap-4
                  rounded-2xl
                  border
                  p-4
                  text-left
                  shadow-sm
                  transition
                  hover:-translate-y-0.5
                  hover:shadow-md
                  ${
                    operationalFilter ===
                    'no_scheduled_return'
                      ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-200'
                      : 'border-orange-200 bg-white'
                  }
                `}
              >
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Sem retorno agendado
                  </p>

                  <p className="mt-1 text-2xl font-bold text-orange-800">
                    {operationalSummary.noScheduledReturn}
                  </p>
                </div>

                <span className="rounded-xl bg-orange-100 p-2 text-orange-700">
                  <CalendarDays size={21} />
                </span>
              </button>

              <button
                type="button"
                onClick={() =>
                  toggleOperationalFilter(
                    'next_set_received'
                  )
                }
                className={`
                  flex
                  items-center
                  justify-between
                  gap-4
                  rounded-2xl
                  border
                  p-4
                  text-left
                  shadow-sm
                  transition
                  hover:-translate-y-0.5
                  hover:shadow-md
                  ${
                    operationalFilter ===
                    'next_set_received'
                      ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-blue-200 bg-white'
                  }
                `}
              >
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Alinhadores na clínica
                  </p>

                  <p className="mt-1 text-2xl font-bold text-blue-800">
                    {
                      operationalSummary.nextSetWaitingDelivery
                    }
                  </p>
                </div>

                <span className="rounded-xl bg-blue-100 p-2 text-blue-700">
                  <CheckCircle2 size={21} />
                </span>
              </button>

              <button
                type="button"
                onClick={() =>
                  toggleOperationalFilter(
                    'initial_actions_pending'
                  )
                }
                className={`
                  flex
                  items-center
                  justify-between
                  gap-4
                  rounded-2xl
                  border
                  p-4
                  text-left
                  shadow-sm
                  transition
                  hover:-translate-y-0.5
                  hover:shadow-md
                  ${
                    operationalFilter ===
                    'initial_actions_pending'
                      ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200'
                      : 'border-amber-200 bg-white'
                  }
                `}
              >
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Ações iniciais pendentes
                  </p>

                  <p className="mt-1 text-2xl font-bold text-amber-800">
                    {
                      operationalSummary.initialActionsPending
                    }
                  </p>
                </div>

                <span className="rounded-xl bg-amber-100 p-2 text-amber-700">
                  <AlertCircle size={21} />
                </span>
              </button>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Activity
                      size={18}
                      className="text-[var(--clinic-primary)]"
                    />

                    <h2 className="font-semibold text-gray-900">
                      Produção da Central
                    </h2>
                  </div>

                  <p className="mt-1 text-sm text-gray-500">
                    Visão operacional dos tratamentos que exigem
                    atenção ou atividade da clínica.
                  </p>
                </div>

                {(operationalFilter !== 'all' ||
                  recommendedActionFilter ===
                    'any_pending') && (
                  <button
                    type="button"
                    onClick={() => {
                      setOperationalFilter('all');

                      if (
                        recommendedActionFilter ===
                        'any_pending'
                      ) {
                        setRecommendedActionFilter(
                          'all'
                        );
                      }
                    }}
                    className="
                      inline-flex
                      items-center
                      gap-1.5
                      rounded-lg
                      px-2.5
                      py-1.5
                      text-xs
                      font-semibold
                      text-gray-600
                      hover:bg-gray-100
                    "
                  >
                    <X size={14} />

                    Remover seleção
                  </button>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {Object.entries(
                  PRODUCTION_PANEL_CARDS
                ).map(([cardId, config]) => {
                  const ProductionIcon =
                    config.icon;

                  const values = {
                    waiting_delivery:
                      productionSummary.waitingDelivery,

                    returns_today:
                      productionSummary.returnsToday,

                    delayed:
                      productionSummary.delayed,

                    pending_messages:
                      productionSummary.pendingMessages,

                    active_treatments:
                      productionSummary.activeTreatments,
                  };

                  const isActive =
                    cardId === 'pending_messages'
                      ? recommendedActionFilter ===
                        'any_pending'
                      : operationalFilter ===
                        config.operationalFilter;

                  return (
                    <button
                      key={cardId}
                      type="button"
                      onClick={() =>
                        handleProductionCardClick(
                          cardId
                        )
                      }
                      className={`
                        flex
                        min-h-[132px]
                        flex-col
                        justify-between
                        rounded-2xl
                        border
                        p-3
                        text-left
                        shadow-sm
                        transition
                        hover:-translate-y-0.5
                        hover:shadow-md
                        ${config.cardClass}
                        ${
                          isActive
                            ? 'ring-2 ring-[var(--clinic-primary)] ring-offset-2'
                            : ''
                        }
                      `}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={`
                            flex
                            h-10
                            w-10
                            items-center
                            justify-center
                            rounded-xl
                            ${config.iconClass}
                          `}
                        >
                          <ProductionIcon size={19} />
                        </span>

                        <span
                          className={`
                            text-3xl
                            font-bold
                            ${config.valueClass}
                          `}
                        >
                          {values[cardId] || 0}
                        </span>
                      </div>

                      <div className="mt-3">
                        <p className="text-sm font-semibold leading-tight text-gray-900">
                          {config.label}
                        </p>

                        <p className="mt-1 text-[11px] leading-snug text-gray-600">
                          {config.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2
                      size={18}
                      className="text-[var(--clinic-primary)]"
                    />

                    <h2 className="font-semibold text-gray-900">
                      Saúde da carteira
                    </h2>
                  </div>

                  <p className="mt-1 text-sm text-gray-500">
                    Visão consolidada da qualidade operacional dos
                    acompanhamentos com alinhadores.
                  </p>
                </div>

                {healthFilter !== 'all' && (
                  <button
                    type="button"
                    onClick={() =>
                      setHealthFilter('all')
                    }
                    className="
                      inline-flex
                      items-center
                      gap-1.5
                      rounded-lg
                      px-2.5
                      py-1.5
                      text-xs
                      font-semibold
                      text-gray-600
                      hover:bg-gray-100
                    "
                  >
                    <X size={14} />

                    Remover seleção
                  </button>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <div
                  className="
                    flex
                    min-h-[132px]
                    flex-col
                    justify-between
                    rounded-2xl
                    border
                    border-purple-200
                    bg-purple-50
                    p-3
                    shadow-sm
                  "
                >
                  <div className="flex items-start justify-between gap-3">
                    <span
                      className="
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-xl
                        bg-purple-100
                        text-purple-700
                      "
                    >
                      <Activity size={19} />
                    </span>

                    <span className="text-3xl font-bold text-purple-900">
                      {healthSummary.averageScore !== null
                        ? healthSummary.averageScore
                        : '—'}
                    </span>
                  </div>

                  <div className="mt-3">
                    <p className="text-sm font-semibold leading-tight text-gray-900">
                      Score médio
                    </p>

                    <p className="mt-1 text-[11px] leading-snug text-gray-600">
                      Média de {healthSummary.evaluated}{' '}
                      {healthSummary.evaluated === 1
                        ? 'tratamento avaliado'
                        : 'tratamentos avaliados'}
                    </p>
                  </div>
                </div>

                {Object.entries(
                  HEALTH_PANEL_CARDS
                ).map(([cardId, config]) => {
                  const HealthCardIcon =
                    config.icon;

                  const values = {
                    critical:
                      healthSummary.critical,

                    attention:
                      healthSummary.attention,

                    healthy:
                      healthSummary.healthy,

                    not_scored:
                      healthSummary.notScored,
                  };

                  const isActive =
                    healthFilter === cardId;

                  return (
                    <button
                      key={cardId}
                      type="button"
                      onClick={() =>
                        toggleHealthFilter(cardId)
                      }
                      className={`
                        flex
                        min-h-[132px]
                        flex-col
                        justify-between
                        rounded-2xl
                        border
                        p-3
                        text-left
                        shadow-sm
                        transition
                        hover:-translate-y-0.5
                        hover:shadow-md
                        ${config.cardClass}
                        ${
                          isActive
                            ? 'ring-2 ring-[var(--clinic-primary)] ring-offset-2'
                            : ''
                        }
                      `}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={`
                            flex
                            h-10
                            w-10
                            items-center
                            justify-center
                            rounded-xl
                            ${config.iconClass}
                          `}
                        >
                          <HealthCardIcon size={19} />
                        </span>

                        <span
                          className={`
                            text-3xl
                            font-bold
                            ${config.valueClass}
                          `}
                        >
                          {values[cardId] || 0}
                        </span>
                      </div>

                      <div className="mt-3">
                        <p className="text-sm font-semibold leading-tight text-gray-900">
                          {config.label}
                        </p>

                        <p className="mt-1 text-[11px] leading-snug text-gray-600">
                          {config.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Activity
                      size={18}
                      className="text-[var(--clinic-primary)]"
                    />

                    <h2 className="font-semibold text-gray-900">
                      Ações recomendadas
                    </h2>
                  </div>

                  <p className="mt-1 text-sm text-gray-500">
                    Selecione uma ação para visualizar apenas os
                    pacientes que precisam dessa intervenção.
                  </p>
                </div>

                {recommendedActionFilter !== 'all' && (
                  <button
                    type="button"
                    onClick={() =>
                      setRecommendedActionFilter('all')
                    }
                    className="
                      inline-flex
                      items-center
                      gap-1.5
                      rounded-lg
                      px-2.5
                      py-1.5
                      text-xs
                      font-semibold
                      text-gray-600
                      hover:bg-gray-100
                    "
                  >
                    <X size={14} />

                    Remover seleção
                  </button>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {Object.entries(
                  RECOMMENDED_ACTION_CARDS
                ).map(([actionId, config]) => {
                  const ActionIcon = config.icon;

                  const isActive =
                    recommendedActionFilter === actionId;

                  const actionCount =
                    recommendedActionSummary[actionId] || 0;

                  return (
                    <button
                      key={actionId}
                      type="button"
                      onClick={() =>
                        toggleRecommendedActionFilter(
                          actionId
                        )
                      }
                      className={`
                        flex
                        min-h-[128px]
                        flex-col
                        justify-between
                        rounded-2xl
                        border
                        p-3
                        text-left
                        shadow-sm
                        transition
                        hover:-translate-y-0.5
                        hover:shadow-md
                        ${config.cardClass}
                        ${
                          isActive
                            ? 'ring-2 ring-[var(--clinic-primary)] ring-offset-2'
                            : ''
                        }
                      `}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={`
                            flex
                            h-9
                            w-9
                            items-center
                            justify-center
                            rounded-xl
                            ${config.iconClass}
                          `}
                        >
                          <ActionIcon size={18} />
                        </span>

                        <span
                          className={`
                            text-2xl
                            font-bold
                            ${config.valueClass}
                          `}
                        >
                          {actionCount}
                        </span>
                      </div>

                      <div className="mt-3">
                        <p className="text-sm font-semibold leading-tight">
                          {config.label}
                        </p>

                        <p className="mt-1 text-[11px] leading-snug opacity-75">
                          {config.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <Filter size={18} className="text-gray-500" />

                <h2 className="font-semibold text-gray-900">
                  Filtros
                </h2>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-12">
                <label className="relative sm:col-span-2 xl:col-span-3">
                  <Search
                    size={18}
                    className="
                      pointer-events-none
                      absolute
                      left-3
                      top-1/2
                      -translate-y-1/2
                      text-gray-400
                    "
                  />

                  <input
                    type="text"
                    value={search}
                    onChange={(event) =>
                      setSearch(event.target.value)
                    }
                    placeholder="Buscar paciente, telefone ou e-mail"
                    className="
                      w-full
                      min-w-0
                      rounded-xl
                      border
                      border-gray-300
                      py-2
                      pl-10
                      pr-3
                      text-sm
                      outline-none
                      focus:border-[var(--clinic-primary)]
                    "
                  />
                </label>

                <select
                  value={professionalFilter}
                  onChange={(event) =>
                    setProfessionalFilter(event.target.value)
                  }
                  className="w-full min-w-0 rounded-xl border border-gray-300 px-3 py-2 text-sm xl:col-span-2"
                >
                  <option value="all">
                    Todos os profissionais
                  </option>

                  {professionals.map((professional) => (
                    <option
                      key={professional.id}
                      value={professional.id}
                    >
                      {professional.name}
                    </option>
                  ))}
                </select>

                <select
                  value={providerFilter}
                  onChange={(event) =>
                    setProviderFilter(event.target.value)
                  }
                  className="w-full min-w-0 rounded-xl border border-gray-300 px-3 py-2 text-sm xl:col-span-2"
                >
                  <option value="all">
                    Todos os fabricantes
                  </option>

                  {providers.map((provider) => (
                    <option key={provider} value={provider}>
                      {provider}
                    </option>
                  ))}
                </select>

                <select
                  value={treatmentStatusFilter}
                  onChange={(event) =>
                    setTreatmentStatusFilter(event.target.value)
                  }
                  className="w-full min-w-0 rounded-xl border border-gray-300 px-3 py-2 text-sm xl:col-span-2"
                >
                  <option value="all">
                    Todos os status
                  </option>

                  {Object.entries(TREATMENT_STATUS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </select>

                <select
                  value={operationalFilter}
                  onChange={(event) =>
                    setOperationalFilter(event.target.value)
                  }
                  className="w-full min-w-0 rounded-xl border border-gray-300 px-3 py-2 text-sm xl:col-span-3"
                >
                  {Object.entries(OPERATIONAL_FILTERS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </select>

                <select
                  value={recommendedActionFilter}
                  onChange={(event) =>
                    setRecommendedActionFilter(
                      event.target.value
                    )
                  }
                  className="
                    w-full
                    min-w-0
                    rounded-xl
                    border
                    border-gray-300
                    px-3
                    py-2
                    text-sm
                    xl:col-span-3
                  "
                >
                  {Object.entries(
                    RECOMMENDED_ACTION_FILTERS
                  ).map(([value, label]) => (
                    <option key={value} value={value}>
                      Ação: {label}
                    </option>
                  ))}
                </select>

                <select
                  value={healthFilter}
                  onChange={(event) =>
                    setHealthFilter(event.target.value)
                  }
                  className="
                    w-full
                    min-w-0
                    rounded-xl
                    border
                    border-gray-300
                    px-3
                    py-2
                    text-sm
                    xl:col-span-3
                  "
                >
                  {Object.entries(
                    HEALTH_FILTERS
                  ).map(([value, label]) => (
                    <option key={value} value={value}>
                      Saúde: {label}
                    </option>
                  ))}
                </select>

                <select
                  value={sortBy}
                  onChange={(event) =>
                    setSortBy(event.target.value)
                  }
                  className="
                    w-full
                    min-w-0
                    rounded-xl
                    border
                    border-gray-300
                    px-3
                    py-2
                    text-sm
                    xl:col-span-3
                  "
                >
                  {Object.entries(SORT_OPTIONS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        Ordenar: {label}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="mt-3 space-y-2">
                {(monitoringFilter !== 'all' ||
                  operationalFilter !== 'all' ||
                  recommendedActionFilter !== 'all' ||
                  healthFilter !== 'all') && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-gray-500">
                      Filtros ativos:
                    </span>

                    {monitoringFilter !== 'all' && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-[11px] font-semibold text-green-800">
                        Situação:{' '}
                        {monitoringFilter === 'not_on_time'
                          ? 'Não estão em dia'
                          : MONITORING_CONFIG[monitoringFilter]?.label || monitoringFilter}
                        <button type="button" onClick={() => setMonitoringFilter('all')} className="rounded-full p-0.5 hover:bg-green-200" aria-label="Remover filtro de situação">
                          <X size={12} />
                        </button>
                      </span>
                    )}

                    {operationalFilter !== 'all' && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-100 px-2.5 py-1 text-[11px] font-semibold text-cyan-800">
                        Operação: {OPERATIONAL_FILTERS[operationalFilter] || operationalFilter}
                        <button type="button" onClick={() => setOperationalFilter('all')} className="rounded-full p-0.5 hover:bg-cyan-200" aria-label="Remover filtro operacional">
                          <X size={12} />
                        </button>
                      </span>
                    )}

                    {recommendedActionFilter !== 'all' && (
                      <span
                        className="
                          inline-flex
                          items-center
                          gap-1.5
                          rounded-full
                          bg-blue-100
                          px-2.5
                          py-1
                          text-[11px]
                          font-semibold
                          text-blue-800
                        "
                      >
                        Ação:{' '}
                        {
                          RECOMMENDED_ACTION_FILTERS[
                            recommendedActionFilter
                          ]
                        }

                        <button
                          type="button"
                          onClick={() =>
                            setRecommendedActionFilter('all')
                          }
                          className="rounded-full p-0.5 hover:bg-blue-200"
                          aria-label="Remover filtro de ação recomendada"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )}

                    {healthFilter !== 'all' && (
                      <span
                        className="
                          inline-flex
                          items-center
                          gap-1.5
                          rounded-full
                          bg-purple-100
                          px-2.5
                          py-1
                          text-[11px]
                          font-semibold
                          text-purple-800
                        "
                      >
                        Saúde: {HEALTH_FILTERS[healthFilter]}

                        <button
                          type="button"
                          onClick={() =>
                            setHealthFilter('all')
                          }
                          className="rounded-full p-0.5 hover:bg-purple-200"
                          aria-label="Remover filtro de saúde"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )}

                    {(
                      search ||
                      professionalFilter !== 'all' ||
                      providerFilter !== 'all' ||
                      treatmentStatusFilter !== 'all' ||
                      monitoringFilter !== 'all' ||
                      operationalFilter !== 'all' ||
                      recommendedActionFilter !== 'all' ||
                      healthFilter !== 'all' ||
                      sortBy !== 'priority'
                    ) && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="
                          text-xs
                          font-semibold
                          text-[var(--clinic-primary)]
                          hover:underline
                        "
                      >
                        Limpar filtros
                      </button>
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Exibindo{' '}
                  <strong>{sortedItems.length}</strong> de{' '}
                  <strong>{items.length}</strong> tratamentos.
                </p>
              </div>
            </section>

          </>
        )}

        <section className="space-y-4">
          {loading ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-gray-500">
              Carregando tratamentos...
            </div>
          ) : sortedItems.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
              <Activity
                className="mx-auto text-gray-400"
                size={36}
              />

              <p className="mt-3 font-semibold text-gray-700">
                {embedded
                  ? 'Nenhum tratamento com alinhadores registrado'
                  : 'Nenhum tratamento encontrado'}
              </p>

              <p className="mt-1 text-sm text-gray-500">
                {embedded
                  ? 'Use o botão “Novo tratamento” para iniciar o acompanhamento deste paciente.'
                  : 'Ajuste os filtros ou atualize os dados.'}
              </p>
            </div>
          ) : (
            sortedItems.map((item) => {
              const monitoring =
                MONITORING_CONFIG[
                  item.monitoring_status
                ] || MONITORING_CONFIG.no_reference;

              const MonitoringIcon = monitoring.icon;

              const treatmentProgress =
                calculateProgress(item);

              const effectiveDays =
                item.effective_follow_up_interval_days ||
                item.follow_up_interval_days ||
                90;

              const nextSetWaitingDelivery =
                item.next_set_received === true;

              const treatmentCardClass =
                getTreatmentCardClass(
                  item,
                  nextSetWaitingDelivery
                );

              const recommendedAction =
                getRecommendedActionSummary(item);

              const recommendedActionTone =
                RECOMMENDED_ACTION_TONES[
                  recommendedAction.tone
                ] ||
                RECOMMENDED_ACTION_TONES.gray;

              return (
                <article
                  key={item.id}
                  className={`
                    relative
                    overflow-hidden
                    rounded-2xl
                    border
                    p-4
                    shadow-sm
                    transition
                    hover:shadow-md
                    md:p-5
                    ${treatmentCardClass}
                  `}
                >

                  {item.monitoring_status === 'urgent' && (
                    <div
                      className="
                        absolute
                        bottom-0
                        left-0
                        top-0
                        w-1.5
                        bg-red-500
                      "
                    />
                  )}

                  {item.monitoring_status === 'overdue' && (
                    <div
                      className="
                        absolute
                        bottom-0
                        left-0
                        top-0
                        w-1
                        bg-orange-500
                      "
                    />
                  )}

                  {item.monitoring_status === 'attention' && (
                    <div
                      className="
                        absolute
                        bottom-0
                        left-0
                        top-0
                        w-1
                        bg-amber-400
                      "
                    />
                  )}

                  {item.monitoring_status ===
                    'needs_review' && (
                    <div
                      className="
                        absolute
                        bottom-0
                        left-0
                        top-0
                        w-1
                        bg-purple-500
                      "
                    />
                  )}

                  <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                            <UserRound size={22} />
                          </div>

                          <div className="min-w-0">
                            <h3 className="truncate text-lg font-bold text-gray-900">
                              {item.patient_name}
                            </h3>

                            <p className="text-sm text-gray-500">
                              {item.provider ||
                                'Fabricante não informado'}
                              {' · '}
                              {item.professional_name ||
                                'Profissional não informado'}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                          <span className="inline-flex w-fit items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                            {CURRENT_SITUATIONS[
                              item.phase_status === 'final_follow_up'
                                ? 'follow_up_only'
                                : item.phase_status
                            ] || TREATMENT_STATUS[item.status] || 'Em acompanhamento'}
                          </span>

                          {hasAllAlignersDelivered(item) && (
                            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
                              <CheckCircle2 size={14} />
                              Todos os alinhadores entregues
                            </span>
                          )}

                          {nextSetWaitingDelivery && (
                            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
                              <CheckCircle2 size={14} />

                              {item.next_set_start_stage !== null &&
                              item.next_set_start_stage !== undefined &&
                              item.next_set_end_stage !== null &&
                              item.next_set_end_stage !== undefined
                                ? `Na clínica: ${item.next_set_start_stage}–${item.next_set_end_stage}${
                                    item.total_stages
                                      ? ` de ${item.total_stages}`
                                      : ''
                                  }`
                                : 'Próximos alinhadores chegaram'}
                            </span>
                          )}

                          <span
                            className={`
                              inline-flex
                              w-fit
                              items-center
                              gap-1.5
                              rounded-full
                              px-3
                              py-1
                              text-xs
                              font-semibold
                              ${monitoring.badgeClass}
                            `}
                          >
                            <MonitoringIcon size={15} />

                            {item.monitoring_label ||
                              monitoring.label}
                          </span>
                        </div>
                      </div>

                      {item.monitoring_status === 'urgent' && (
                        <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-100/70 px-3 py-2.5 text-sm text-red-900">
                          <ShieldAlert
                            size={18}
                            className="mt-0.5 shrink-0"
                          />

                          <div>
                            <p className="font-semibold">
                              Acompanhamento urgente
                            </p>

                            <p className="mt-0.5 text-xs text-red-700">
                              O prazo configurado para intervenção
                              urgente foi ultrapassado. Priorize o
                              contato ou o agendamento deste
                              paciente.
                            </p>
                          </div>
                        </div>
                      )}

                      {item.monitoring_status === 'overdue' && (
                        <div className="mt-4 flex items-start gap-2 rounded-xl border border-orange-200 bg-orange-100/60 px-3 py-2.5 text-sm text-orange-900">
                          <AlertCircle
                            size={18}
                            className="mt-0.5 shrink-0"
                          />

                          <div>
                            <p className="font-semibold">
                              Retorno clínico vencido
                            </p>

                            <p className="mt-0.5 text-xs text-orange-700">
                              A data recomendada para revisão já
                              passou. Verifique o acompanhamento e
                              a existência de retorno agendado.
                            </p>
                          </div>
                        </div>
                      )}

                      <details className="mt-4 rounded-xl border border-gray-200 bg-white/70">
                        <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-gray-700">
                          Ver detalhes do tratamento
                          <span className="ml-2 text-xs font-normal text-gray-400">
                            progresso, prazos e conjuntos
                          </span>
                        </summary>

                        <div className="border-t border-gray-200 p-4">
                      <div>
                        <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                          <span className="font-medium text-gray-700">
                            Progresso operacional
                          </span>

                          <span className="text-gray-500">
                            {getOperationalCurrentStage(
                              item
                            )}{' '}
                            de{' '}
                            {Number(
                              item.total_stages || 0
                            )}{' '}
                            ({treatmentProgress}%)
                          </span>
                        </div>

                        <div className="h-2.5 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="
                              h-full
                              rounded-full
                              bg-[var(--clinic-primary)]
                              transition-all
                            "
                            style={{
                              width: `${treatmentProgress}%`,
                            }}
                          />
                        </div>
                      </div>

                      <TreatmentOperationalTimeline
                        item={item}
                      />

                      <TreatmentHealthDiagnosis
                        item={item}
                      />

                      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="rounded-xl bg-gray-50 p-3">
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Última referência
                          </p>

                          <p className="mt-1 font-semibold text-gray-800">
                            {formatDateBR(
                              item.monitoring_reference_date
                            )}
                          </p>

                          <p className="mt-1 text-xs text-gray-500">
                            {REFERENCE_SOURCE[
                              item.monitoring_reference_source
                            ] ||
                              item.monitoring_reference_source_label ||
                              'Origem não informada'}
                          </p>
                        </div>

                        <div className="rounded-xl bg-gray-50 p-3">
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Próxima ação sugerida
                          </p>

                          <p className="mt-1 font-semibold text-gray-800">
                            {formatDateBR(
                              item.next_recommended_review_date
                            )}
                          </p>

                          <p
                            className={`
                              mt-1
                              text-xs
                              ${
                                differenceInCalendarDays(
                                  item.next_recommended_review_date
                                ) < 0
                                  ? 'font-semibold text-red-600'
                                  : 'text-gray-500'
                              }
                            `}
                          >
                            {getDeadlineText(
                              item.next_recommended_review_date
                            )}
                          </p>

                          <p className="mt-1 text-[11px] text-gray-400">
                            Intervalo calculado:{' '}
                            {effectiveDays} dias
                          </p>
                        </div>

                        <div className="rounded-xl bg-gray-50 p-3">
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Retorno
                          </p>

                          <p className="mt-1 font-semibold text-gray-800">
                            {item.has_scheduled_return
                              ? formatDateBR(
                                  item.next_appointment
                                    ?.appointment_date
                                )
                              : 'Não agendado'}
                          </p>

                          <p className="mt-1 text-xs text-gray-500">
                            {item.has_scheduled_return
                              ? 'Consulta futura encontrada na agenda'
                              : 'Paciente ainda sem retorno futuro'}
                          </p>
                        </div>
                        <div className="rounded-xl bg-gray-50 p-3">
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Conjuntos
                          </p>

                          <p className="mt-1 text-[11px] text-gray-400">
                            Atual = entregue ao paciente
                          </p>

                          <p className="mt-1 font-semibold text-gray-800">
                            {item.current_set_start_stage !== null &&
                            item.current_set_start_stage !== undefined
                              ? `Atual: ${item.current_set_start_stage} a ${
                                  item.current_set_end_stage ?? '—'
                                }`
                              : 'Atual não informado'}
                          </p>

                          <p className="mt-1 text-xs text-gray-600">
                            {item.next_set_start_stage !== null &&
                            item.next_set_start_stage !== undefined
                              ? `Próximo: ${item.next_set_start_stage} a ${
                                  item.next_set_end_stage ?? '—'
                                }`
                              : 'Próximo não informado'}
                          </p>

                          <p className="mt-1 text-xs text-gray-500">
                            {hasAllAlignersDelivered(item)
                              ? 'Todos os alinhadores já foram entregues ao paciente'
                              : item.next_set_received
                              ? item.next_set_received_at
                                ? `Próximo conjunto recebido em ${formatDateBR(
                                    item.next_set_received_at
                                  )}`
                                : 'Próximo conjunto recebido, sem data informada'
                              : 'Próximo conjunto não confirmado'}
                          </p>

                          {hasInitialActionsPending(item) && (
                            <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                              <AlertCircle size={13} />

                              Ações iniciais pendentes
                            </p>
                          )}
                        </div>
                      </div>
                        </div>
                      </details>

                      <div
                        className={`
                          mt-4
                          flex
                          flex-col
                          gap-3
                          rounded-xl
                          border
                          p-3
                          sm:flex-row
                          sm:items-center
                          sm:justify-between
                          ${recommendedActionTone.container}
                        `}
                      >
                        <div className="flex min-w-0 items-start gap-3">
                          <span
                            className={`
                              mt-0.5
                              flex
                              h-9
                              w-9
                              shrink-0
                              items-center
                              justify-center
                              rounded-xl
                              ${recommendedActionTone.icon}
                            `}
                          >
                            <Activity size={18} />
                          </span>

                          <div className="min-w-0">
                            <p
                              className={`
                                text-[11px]
                                font-bold
                                uppercase
                                tracking-wide
                                ${recommendedActionTone.description}
                              `}
                            >
                              Próxima ação recomendada
                            </p>

                            <p
                              className={`
                                mt-0.5
                                font-semibold
                                ${recommendedActionTone.title}
                              `}
                            >
                              {recommendedAction.title}
                            </p>

                            <p
                              className={`
                                mt-1
                                text-xs
                                leading-relaxed
                                ${recommendedActionTone.description}
                              `}
                            >
                              {recommendedAction.description}
                            </p>
                          </div>
                        </div>

                        {recommendedAction.templateId && (
                        <button
                          type="button"
                          onClick={() =>
                            openWhatsApp(item)
                          }
                          className="
                            inline-flex
                            shrink-0
                            items-center
                            justify-center
                            gap-2
                            rounded-xl
                            border
                            border-white/70
                            bg-white
                            px-3
                            py-2
                            text-sm
                            font-semibold
                            text-gray-800
                            shadow-sm
                            hover:bg-gray-50
                          "
                        >
                          <MessageCircle size={16} />

                          Executar ação
                        </button>
                        )}
                      </div>
                    </div>

                    <div className="grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-4 xl:w-48 xl:grid-cols-1">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedFollowUpTreatment(item)
                        }
                        className="col-span-2 inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--clinic-primary)] px-3 py-2.5 text-sm font-semibold text-white hover:opacity-90 sm:col-span-1"
                      >
                        <Activity size={17} />
                        <span>Registrar acompanhamento</span>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          openWhatsApp(item)
                        }
                        className="
                          inline-flex
                          items-center
                          justify-center
                          gap-2
                          rounded-xl
                          border
                          border-green-200
                          bg-green-50
                          px-3
                          py-2
                          text-sm
                          font-semibold
                          text-green-800
                          hover:bg-green-100
                        "
                      >
                        <MessageCircle size={17} />

                        <span>
                          Mensagens
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => openAgenda(item)}
                        className="
                          inline-flex
                          items-center
                          justify-center
                          gap-2
                          rounded-xl
                          border
                          border-blue-200
                          bg-blue-50
                          px-3
                          py-2
                          text-sm
                          font-semibold
                          text-blue-800
                          hover:bg-blue-100
                        "
                      >
                        <CalendarDays size={17} />

                        <span>
                          Agendar
                        </span>
                      </button>

                      {item.next_set_received === true &&
                        item.next_set_start_stage !== null &&
                        item.next_set_start_stage !== undefined &&
                        item.next_set_end_stage !== null &&
                        item.next_set_end_stage !== undefined && (
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedSetDeliveryTreatment(
                                item
                              )
                            }
                            className="
                              inline-flex
                              items-center
                              justify-center
                              gap-2
                              rounded-xl
                              border
                              border-purple-200
                              bg-purple-50
                              px-3
                              py-2
                              text-sm
                              font-semibold
                              text-purple-800
                              hover:bg-purple-100
                            "
                          >
                            <CheckCircle2 size={17} />

                            <span>
                              Entregar conjunto
                            </span>
                          </button>
                        )}

                      {typeof renderExtraActions ===
                        'function' &&
                        renderExtraActions(item)}

                      {!embedded && (
                        <button
                          type="button"
                          onClick={() =>
                            openPatientRecord(item)
                          }
                          className="
                            inline-flex
                            items-center
                            justify-center
                            gap-2
                            rounded-xl
                            border
                            border-gray-200
                            bg-white
                            px-3
                            py-2
                            text-sm
                            font-semibold
                            text-gray-700
                            hover:bg-gray-50
                          "
                        >
                          <ExternalLink size={17} />

                         <span>
                            Prontuário
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedMonitoringTreatment(item)
                        }
                        className={`
                          inline-flex
                          items-center
                          justify-center
                          gap-2
                          rounded-xl
                          px-3
                          py-2
                          text-sm
                          font-semibold
                          border
                          border-gray-200
                          bg-white
                          text-gray-700
                          hover:bg-gray-50
                        `}
                      >
                        <Settings2 size={17} />

                        <span>
                          Configurar
                        </span>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </section>
      </div>

      {selectedWhatsAppTreatment && (
        <WhatsAppMessageModal
          item={selectedWhatsAppTreatment}
          onClose={() =>
            setSelectedWhatsAppTreatment(null)
          }
          onUpdated={refreshAfterMutation}
          toast={toast}
        />
      )}

      {selectedSetDeliveryTreatment && (
        <SetDeliveryModal
          item={selectedSetDeliveryTreatment}
          onClose={() =>
            setSelectedSetDeliveryTreatment(null)
          }
          onDelivered={refreshAfterMutation}
          onOpenAgenda={openAgenda}
          toast={toast}
        />
      )}

      {selectedMonitoringTreatment && (
        <MonitoringModal
          item={selectedMonitoringTreatment}
          onClose={() =>
            setSelectedMonitoringTreatment(null)
          }
          onSaved={refreshAfterMutation}
          toast={toast}
        />
      )}

      {selectedFollowUpTreatment && (
        <FollowUpModal
          item={selectedFollowUpTreatment}
          onClose={() =>
            setSelectedFollowUpTreatment(null)
          }
          onSaved={refreshAfterMutation}
          toast={toast}
        />
      )}
    </PageWrapper>
  );
};

export { AlignersPage };
export default AlignersPage;
